import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { collection, getDocs, getDoc, onSnapshot, doc, setDoc } from "firebase/firestore";
import { signInWithCustomToken, onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";
import { CATEGORIES, CATEGORY_FALLBACK_IMAGE, getDisplayCategory } from "../constants/categories";
import { getStoreCallHref } from "../lib/storeCalling";

const BUY_AGAIN_KEY    = "kirana_buy_again";
const ORDER_HISTORY_KEY = "kiranaos_orders";
const CUSTOMER_KEY      = "kiranaos_customer";
const INSTALL_PROMPT_SEEN_KEY = "kiranaos_install_prompt_seen";
const CUSTOMER_LANG_KEY = "kirana_customer_lang";
const PRODUCTS_CACHE_KEY = "kiranaos_products_cache"; // { [storeId]: { savedAt, products } }
const PRODUCTS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;    // serve stale for a day, always revalidate

// Last-known product list for a store, used to paint the storefront immediately
// on a repeat visit while the live Firestore read revalidates in the background.
// Cache misses are silent: any problem just falls back to the loading skeleton.
function readProductsCache(storeId) {
  try {
    const all = JSON.parse(localStorage.getItem(PRODUCTS_CACHE_KEY) || "{}");
    const entry = all[storeId];
    if (!entry || !Array.isArray(entry.products) || !entry.products.length) return null;
    if (Date.now() - Number(entry.savedAt || 0) > PRODUCTS_CACHE_TTL_MS) return null;
    return entry.products;
  } catch { return null; }
}

function writeProductsCache(storeId, products) {
  try {
    const all = JSON.parse(localStorage.getItem(PRODUCTS_CACHE_KEY) || "{}");
    // Keep only this store's list plus the most recent other one, so a customer
    // browsing several stores can't grow this past the localStorage quota.
    const others = Object.entries(all)
      .filter(([id]) => id !== storeId)
      .sort((a, b) => Number(b[1]?.savedAt || 0) - Number(a[1]?.savedAt || 0))
      .slice(0, 1);
    const next = Object.fromEntries(others);
    next[storeId] = { savedAt: Date.now(), products };
    localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(next));
  } catch { /* quota exceeded or private mode, cache is best-effort */ }
}

// Customer-facing UI copy (scoped: nav, home sections, cart, search, order status).
// Falls back to English for any missing key.
const CCOPY = {
  en: {
    navHome: "Home", navProfile: "Profile", navSearch: "Search", navCategories: "Categories",
    tagline: "Fresh · Local · Fast",
    searchPlaceholder: "Search atta, dal, coke...",
    searchStart: "Start typing to search products",
    callStore: "📞 Can't find it? Call the store",
    shopByCategory: "Shop by Category",
    yourUsuals: "Your Usuals",
    bestSellers: "Best Sellers",
    savedItems: "❤️ Saved Items",
    pairsWell: "Pairs Well With Your Cart",
    completeBasket: "Complete Your Basket",
    cartEmptyTitle: "Your cart is empty",
    cartEmptySub: "Start adding items to place an order",
    browseProducts: "Browse Products",
    total: "Total",
    freeDeliveryEvery: "Free delivery on every order",
    freeDeliveryUnlocked: "🎉 You've unlocked free delivery!",
    statusPlaced: "Order placed! Store is reviewing…",
    statusPacked: "Your order is being packed",
    statusOut: "Your order is out for delivery!",
    track: "Track →",
  },
  hi: {
    navHome: "होम", navProfile: "प्रोफ़ाइल", navSearch: "खोजें", navCategories: "श्रेणियाँ",
    tagline: "ताज़ा · लोकल · फ़ास्ट",
    searchPlaceholder: "आटा, दाल, कोक खोजें...",
    searchStart: "प्रोडक्ट खोजने के लिए टाइप करें",
    callStore: "📞 नहीं मिला? दुकान को कॉल करें",
    shopByCategory: "श्रेणी से खरीदें",
    yourUsuals: "आपकी पसंद",
    bestSellers: "सबसे ज़्यादा बिकने वाले",
    savedItems: "❤️ सेव किए आइटम",
    pairsWell: "आपके कार्ट के साथ अच्छा लगेगा",
    completeBasket: "अपनी टोकरी पूरी करें",
    cartEmptyTitle: "आपका कार्ट खाली है",
    cartEmptySub: "ऑर्डर करने के लिए आइटम जोड़ें",
    browseProducts: "प्रोडक्ट देखें",
    total: "कुल",
    freeDeliveryEvery: "हर ऑर्डर पर फ्री डिलीवरी",
    freeDeliveryUnlocked: "🎉 फ्री डिलीवरी मिल गई!",
    statusPlaced: "ऑर्डर मिल गया! दुकान देख रही है…",
    statusPacked: "आपका ऑर्डर पैक हो रहा है",
    statusOut: "आपका ऑर्डर रास्ते में है!",
    track: "ट्रैक करें →",
  },
};
function cc(lang, key) {
  return (CCOPY[lang] && CCOPY[lang][key]) || CCOPY.en[key] || key;
}
// stores: { phone, name, address, deliveryLocation? }

const API_BASE = "https://kiranaos-api-1036279469035.asia-south1.run.app";
const DEFAULT_OG_IMAGE = "https://kiranaos.in/Focused_in_the_shop_s_quiet_moment.png";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const DEFAULT_MAP_CENTER = { lat: 28.6139, lng: 77.2090 };

// Storefront sections are shown in canonical taxonomy order; any product whose
// stored category is not canonical/aliased is preserved and appended after these.
const CATEGORY_ORDER = CATEGORIES;

const BEST_SELLER_NAMES = [
  "Parle-G", "Maggi", "Amul Butter", "Tata Salt", "Red Bull",
  "Coca Cola", "Britannia Good Day", "Horlicks",
];

const COMPANION_RULES = [
  { match: ["tea", "chai"], suggest: ["milk", "sugar", "biscuit", "rusk"] },
  { match: ["coffee"], suggest: ["milk", "sugar", "biscuit"] },
  { match: ["maggi", "noodle"], suggest: ["ketchup", "sauce", "chips", "cold drink"] },
  { match: ["atta", "flour"], suggest: ["oil", "ghee", "dal", "rice"] },
  { match: ["rice"], suggest: ["dal", "oil", "pickle", "papad"] },
  { match: ["dal", "pulse"], suggest: ["rice", "atta", "oil", "masala"] },
  { match: ["bread"], suggest: ["butter", "jam", "milk", "egg"] },
  { match: ["milk"], suggest: ["bread", "corn flakes", "biscuit", "tea"] },
  { match: ["chips", "namkeen"], suggest: ["cold drink", "biscuit", "chocolate"] },
  { match: ["soap", "shampoo"], suggest: ["toothpaste", "detergent", "handwash"] },
];

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  white:       "#FFFFFF",
  offWhite:    "#F5EBD9",
  black:       "#1A0A00",
  grey100:     "#F3ECE0",
  grey300:     "#D4D3D0",
  grey500:     "#8B6B50",
  orange:      "#E8470A",
  orangeLight: "#FDECE4",
  green:       "#1A6B3C",
  red:         "#DC2626",
  shadowSm:    "0 1px 3px rgba(0,0,0,0.06)",
  shadowMd:    "0 4px 16px rgba(0,0,0,0.08)",
  shadowLg:    "0 8px 32px rgba(0,0,0,0.12)",
  radiusSm:    8,
  radiusMd:    16,
  radiusLg:    24,
  radiusPill:  100,
  ease:        "cubic-bezier(0.4, 0, 0.2, 1)",
  fontBody:    "'DM Sans', -apple-system, sans-serif",
  fontDisplay: "'Fraunces', Georgia, serif",
};

// ─── Auth helpers ─────────────────────────────────────────────────────────────
// Wraps fetch with a single token-refresh retry on 401/403.
// On the first attempt it sends the (possibly cached) ID token.
// If the server returns 401 or 403, it forces a token refresh and retries once.
// This handles the case where the locally cached token has expired but Firebase
// still has a valid refresh token, preventing a spurious OTP prompt.
async function fetchWithAuthRetry(url, options = {}) {
  const getToken = (forceRefresh = false) =>
    auth.currentUser ? auth.currentUser.getIdToken(forceRefresh) : Promise.resolve(null);

  const token = await getToken(false);
  const headers = { ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res = await fetch(url, { ...options, headers });

  if ((res.status === 401 || res.status === 403) && auth.currentUser) {
    // Token may be stale — force refresh once and retry
    try {
      const freshToken = await getToken(true);
      if (freshToken) {
        headers["Authorization"] = `Bearer ${freshToken}`;
        res = await fetch(url, { ...options, headers });
      }
    } catch (refreshErr) {
      console.warn("[KiranaOS] Token refresh failed:", refreshErr.message);
    }
  }

  return res;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function hashRating(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return (4.0 + (h % 10) * 0.1).toFixed(1);
}
function hashReviews(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 17 + name.charCodeAt(i)) >>> 0;
  return `${1 + (h % 95)}k`;
}
function calcUnitPrice(price, unit) {
  if (!unit) return null;
  const u = unit.toLowerCase();
  const num = parseFloat(u);
  if (isNaN(num) || num === 0) return null;
  if (u.includes("kg")) return `₹${(price / num / 10).toFixed(0)} / 100g`;
  if (u.includes("g"))  return `₹${(price / num * 100).toFixed(0)} / 100g`;
  if (u.includes("l") && !u.includes("ml")) return `₹${(price / num / 10).toFixed(0)} / 100ml`;
  if (u.includes("ml")) return `₹${(price / num * 100).toFixed(0)} / 100ml`;
  return null;
}

function productSearchText(product) {
  return `${product?.name || ""} ${product?.category || ""} ${product?.unit || ""}`.toLowerCase();
}

function findMatchingProduct(item, productsById, products) {
  if (!item) return null;
  if (item.id && productsById[item.id]) return productsById[item.id];
  const target = String(item.name || "").trim().toLowerCase();
  if (!target) return null;
  return products.find((p) => String(p.name || "").trim().toLowerCase() === target) || null;
}

function getCompanionProducts(cartItems, products, cart, limit = 8) {
  if (!cartItems.length) return [];
  const cartText = cartItems.map(productSearchText).join(" ");
  const scored = products
    .filter((p) => !cart[p.id] && p.active !== false && p.stock !== 0)
    .map((product) => {
      const text = productSearchText(product);
      let score = 0;
      COMPANION_RULES.forEach((rule) => {
        const matched = rule.match.some((word) => cartText.includes(word));
        if (matched && rule.suggest.some((word) => text.includes(word))) score += 4;
      });
      if (cartItems.some((item) => item.category === product.category)) score += 1;
      if (product.discount > 0) score += 1;
      return { product, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || String(a.product.name).localeCompare(String(b.product.name)));
  return scored.slice(0, limit).map(({ product }) => product);
}

function setHeadTag(selector, { attr, value, content }) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonicalUrl(url) {
  let el = document.head.querySelector("link[rel='canonical']");
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

function isRunningStandalone() {
  return window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
}

function loadGoogleMapsApi(apiKey) {
  if (!apiKey) return Promise.reject(new Error("Google Maps API key is missing."));
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (window.__kiranaGoogleMapsPromise) return window.__kiranaGoogleMapsPromise;

  window.__kiranaGoogleMapsPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById("kirana-google-maps-js");
    const finish = () => {
      if (window.google?.maps?.places) resolve(window.google);
      else reject(new Error("Google Maps Places library did not load."));
    };

    if (existingScript) {
      if (window.google?.maps?.places) {
        resolve(window.google);
        return;
      }
      existingScript.addEventListener("load", finish, { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Google Maps failed to load.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = "kirana-google-maps-js";
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", () => reject(new Error("Google Maps failed to load.")), { once: true });
    document.head.appendChild(script);
  });

  return window.__kiranaGoogleMapsPromise;
}

function normalizeDeliveryLocation(value, fallbackAddress = "") {
  const lat = Number(value?.lat);
  const lng = Number(value?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const addressLine = String(value?.addressLine || value?.address || fallbackAddress || "").trim();
  return {
    lat,
    lng,
    address: addressLine,
    addressLine,
    placeId: value?.placeId || null,
    source: value?.source || "browser_geolocation",
  };
}

function getDeliveryLocationForPayload(location, addressLine) {
  const normalized = normalizeDeliveryLocation(location, addressLine);
  if (!normalized) return null;
  const trimmedAddress = String(addressLine || "").trim();
  return {
    ...normalized,
    address: trimmedAddress,
    addressLine: trimmedAddress,
  };
}

function normalizeMoneySetting(value, fallback) {
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? amount : fallback;
}

function formatMoneyAmount(value) {
  return normalizeMoneySetting(value, 0).toFixed(2).replace(/\.00$/, "");
}

// Treat missing/invalid discounts as 0; clamp to 0-100 so prices never go negative or NaN.
function normalizeDiscountPercent(value) {
  const pct = Number(value);
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  return Math.min(pct, 100);
}

// Original (pre-discount) unit price as a safe number.
function getOriginalUnitPrice(product) {
  const price = Number(product?.price);
  return Number.isFinite(price) && price >= 0 ? price : 0;
}

// Canonical discounted unit price used everywhere cart/order totals are calculated.
function getEffectiveUnitPrice(product) {
  const price = getOriginalUnitPrice(product);
  const pct = normalizeDiscountPercent(product?.discount);
  return price * (1 - pct / 100);
}

// Round money to 2 decimals so float artifacts (e.g. 199 * 0.9 = 179.10000000000002)
// never reach the order payload, Firestore, or WhatsApp notifications.
function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

// Line total for a cart item (effective unit price * quantity).
function getLineTotal(item) {
  const qty = Number(item?.quantity);
  const safeQty = Number.isFinite(qty) && qty > 0 ? qty : 0;
  return getEffectiveUnitPrice(item) * safeQty;
}

// ─── Global CSS ───────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&display=swap');
  * { box-sizing: border-box; }
  html { scroll-behavior: auto !important; -webkit-overflow-scrolling: touch; }
  *::-webkit-scrollbar { display: none; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position:  400px 0; }
  }
  .shimmer {
    background: linear-gradient(90deg, #E8E7E5 25%, #F3ECE0 50%, #E8E7E5 75%);
    background-size: 800px 100%;
    animation: shimmer 1.4s infinite linear;
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fade-up { animation: fadeUp 0.3s ease forwards; }
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
  .slide-up { animation: slideUp 0.35s cubic-bezier(0.4,0,0.2,1) forwards; }
  @keyframes checkPop {
    0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
    70%  { transform: scale(1.15) rotate(4deg); }
    100% { transform: scale(1) rotate(0); opacity: 1; }
  }
  .check-pop { animation: checkPop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; }
  @keyframes confettiFall {
    0%   { transform: translateY(-10px) rotate(0deg); opacity: 1; }
    100% { transform: translateY(120px) rotate(360deg); opacity: 0; }
  }
  .confetti-dot {
    position: absolute;
    width: 8px; height: 8px;
    border-radius: 50%;
    animation: confettiFall 1s ease-out 1 forwards;
  }
  .pac-container {
    z-index: 10000 !important;
    border-radius: 0 0 12px 12px;
    box-shadow: 0 14px 30px rgba(26,10,0,0.14);
    border: 1px solid #E8E7E5;
    overflow: hidden;
    font-family: 'DM Sans', -apple-system, sans-serif;
  }
  .pac-item {
    padding: 10px 12px;
    font-size: 12px;
    cursor: pointer;
  }
  .pac-item-query {
    font-size: 13px;
    color: #1A0A00;
  }
  @keyframes bearRide {
    from { transform: translateX(-200px); }
    to   { transform: translateX(110vw); }
  }
  @keyframes bearOverlayFade {
    0%   { opacity: 1; }
    70%  { opacity: 1; }
    100% { opacity: 0; }
  }
  .bear-ride { animation: bearRide 3s cubic-bezier(0.4, 0, 0.2, 1) forwards; }
  .bear-overlay-fade { animation: bearOverlayFade 4s ease forwards; }
  @keyframes starPop {
    0%   { transform: scale(1); }
    50%  { transform: scale(1.35); }
    100% { transform: scale(1); }
  }
  .star-pop { animation: starPop 0.2s ease; }
  @keyframes bearWobble {
    0%, 100% { transform: translateX(0); }
    50%      { transform: translateX(8px); }
  }
  .bear-wobble { animation: bearWobble 0.6s ease-in-out infinite; }
`;

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page:  { minHeight: "100vh", background: T.offWhite, display: "flex", flexDirection: "column", alignItems: "center", fontFamily: T.fontBody },
  shell: { width: "100%", maxWidth: 480, background: T.white, minHeight: "100vh", position: "relative", display: "flex", flexDirection: "column" },

  // Header
  stickyHeader: { position: "sticky", top: 0, zIndex: 100, background: T.white, borderBottom: `1px solid ${T.grey100}` },
  topBar:       { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px" },
  storeInfo:    { display: "flex", flexDirection: "column", gap: 2 },
  storeName:    { fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 700, color: T.black, margin: 0, lineHeight: 1.2 },
  storeTagline: { fontFamily: T.fontBody, fontSize: 12, color: T.grey500, margin: 0 },
  topRight:     { display: "flex", alignItems: "center", gap: 10 },
  cartBtn:      { position: "relative", background: "transparent", border: "none", cursor: "pointer", padding: 6, lineHeight: 1 },
  cartBadge:    {
    position: "absolute", top: 0, right: 0,
    background: T.orange, color: T.white,
    borderRadius: T.radiusPill, fontSize: 9, fontWeight: 700,
    minWidth: 17, height: 17, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px",
  },

  // Search
  searchWrap: { padding: "6px 12px 8px" },
  searchBar:  {
    display: "flex", alignItems: "center", gap: 10,
    background: T.grey100, borderRadius: T.radiusPill,
    height: 48, padding: "0 16px",
  },
  searchInput: { flex: 1, border: "none", background: "transparent", fontSize: 14, outline: "none", color: T.black, fontFamily: T.fontBody },
  searchIcon:  { color: T.grey500, flexShrink: 0 },
  micIcon:     { color: T.grey500, flexShrink: 0, cursor: "pointer" },

  // Pills
  pillBar: { display: "flex", gap: 6, overflowX: "auto", padding: "4px 12px 8px", scrollbarWidth: "none" },
  pill: (active) => ({
    flexShrink: 0, padding: "6px 14px", borderRadius: T.radiusPill,
    border: `1px solid ${active ? T.black : T.grey300}`,
    background: active ? T.black : T.white,
    color: active ? T.white : T.black,
    fontSize: 13, fontWeight: 400, cursor: "pointer", whiteSpace: "nowrap",
    fontFamily: T.fontBody, transition: `all 0.2s ${T.ease}`,
  }),

  // Hero banner
  heroBanner: {
    margin: "12px 12px 4px",
    background: "linear-gradient(135deg, #E8470A 0%, #FF8A3D 100%)",
    border: "none", borderRadius: 18,
    padding: "14px 18px",
    boxShadow: "0 10px 26px rgba(232,71,10,0.22)",
  },
  heroBannerText: { color: T.white, fontWeight: 700, fontSize: 13.5, fontFamily: T.fontBody, margin: 0, letterSpacing: "0.1px" },

  // Category grid — 2-col, 160px
  catGridTitle: { fontSize: 11, fontWeight: 700, color: T.grey500, margin: "18px 12px 10px", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: T.fontBody },
  catGrid:      { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: "0 12px" },
  catCard:      {
    background: T.white, borderRadius: 20,
    boxShadow: "0 6px 20px rgba(20,10,5,0.06)",
    overflow: "hidden", cursor: "pointer", height: 160,
    display: "flex", flexDirection: "column",
    border: "none", padding: 0, textAlign: "left",
    transition: `transform 0.15s ${T.ease}, box-shadow 0.15s ${T.ease}`,
  },
  catCardImgWrap:     { position: "relative", height: 110, background: "linear-gradient(180deg, #FAF7F4 0%, #FFFFFF 100%)", padding: 12, flexShrink: 0 },
  catCardImg:         { width: "100%", height: "100%", objectFit: "contain", display: "block", mixBlendMode: "multiply" },
  catCardImgFallback: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, color: T.grey300 },
  catCardGradient:    { position: "absolute", bottom: 0, left: 0, right: 0, height: 40, background: "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)", pointerEvents: "none" },
  catCardBody:        { height: 50, padding: "0 12px", display: "flex", alignItems: "center" },
  // Two lines: several real category names ("Breakfast & Instant Food",
  // "Sweets & Confectionery") were being cut mid-word on a single line.
  catCardName:        { fontSize: 13, fontWeight: 700, color: "#1A0A00", fontFamily: T.fontBody, margin: 0, lineHeight: 1.25, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" },

  // Featured horizontal scroll
  featuredTitle:  { fontSize: 18, fontWeight: 700, color: T.black, margin: "18px 12px 10px", fontFamily: T.fontDisplay, letterSpacing: "-0.2px" },
  featuredScroll: { display: "flex", overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", paddingLeft: 16, paddingRight: 0, paddingBottom: 10, marginBottom: 8 },
  featuredCard:   { flexShrink: 0, width: 140, height: "auto", minHeight: 200, marginRight: 12, background: T.white, borderRadius: 18, boxShadow: "0 6px 18px rgba(20,10,5,0.06)", cursor: "pointer", display: "flex", flexDirection: "column", overflow: "hidden" },
  featuredImg:    { width: "100%", height: 100, objectFit: "contain", background: "linear-gradient(180deg, #FAF7F4 0%, #FFFFFF 100%)", display: "block", flexShrink: 0 },
  featuredBody:   { padding: "8px", flexShrink: 0, display: "flex", flexDirection: "column" },
  featuredName:   { fontSize: 12, fontWeight: 500, color: T.black, margin: "0 0 4px", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", fontFamily: T.fontBody, lineHeight: 1.3 },
  featuredPrice:  { fontSize: 13, fontWeight: 700, color: T.black, fontFamily: T.fontBody, marginTop: 4 },

  // Section header
  sectionHeader: { fontSize: 18, fontWeight: 700, color: T.black, margin: "22px 12px 10px", fontFamily: T.fontDisplay, letterSpacing: "-0.2px" },

  // Product grid
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "0 12px" },

  // Product card: compact by design. The old card spent a whole row on the
  // ADD button and another on the unit price, so barely two products fit on a
  // phone screen. Price and ADD now share one row and the unit + unit-price
  // share another, which roughly halves the card height.
  card:             { background: T.white, borderRadius: 14, border: `1px solid ${T.grey100}`, boxShadow: "0 1px 2px rgba(20,10,5,0.04)", overflow: "hidden", display: "flex", flexDirection: "column", position: "relative", transition: `transform 0.15s ${T.ease}, box-shadow 0.15s ${T.ease}` },
  cardImgWrap:      { background: "linear-gradient(180deg, #FAF7F4 0%, #FFFFFF 100%)", position: "relative" },
  cardImg:          { width: "100%", height: 96, objectFit: "contain", background: "transparent", display: "block", mixBlendMode: "multiply" },
  cardImgFallback:  { width: "100%", height: 96, background: T.grey100, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, color: T.grey300 },
  // Discount as a corner flag on the image, the way the big grocery apps do it,
  // instead of a third inline text row under the name.
  cardDiscountFlag: { position: "absolute", top: 0, left: 0, background: "linear-gradient(135deg, #1A6B3C 0%, #00C97F 100%)", color: T.white, fontSize: 9.5, fontWeight: 800, padding: "3px 7px", borderRadius: "0 0 10px 0", fontFamily: T.fontBody, letterSpacing: "0.2px", zIndex: 2 },
  cardMetaRow:      { fontSize: 10.5, color: T.grey500, margin: "0 0 6px", fontFamily: T.fontBody, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  cardFooterRow:    { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 6, marginTop: "auto" },
  cardPriceNow:     { fontSize: 15, fontWeight: 700, color: T.black, fontFamily: T.fontBody, lineHeight: 1.1 },
  cardPriceWas:     { fontSize: 10.5, color: T.grey500, textDecoration: "line-through", fontFamily: T.fontBody, display: "block", lineHeight: 1.2 },
  popularBadge:     { position: "absolute", top: 8, left: 8, background: "linear-gradient(135deg, #E8470A 0%, #FF8A3D 100%)", color: T.white, fontSize: 9, fontWeight: 800, letterSpacing: "0.5px", borderRadius: T.radiusPill, padding: "3px 8px", fontFamily: T.fontBody, boxShadow: "0 3px 8px rgba(232,71,10,0.3)" },
  oosOverlay:       { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: "rgba(26,10,0,0.6)", color: T.white, fontSize: 10, fontWeight: 600, borderRadius: T.radiusSm, padding: "4px 8px", whiteSpace: "nowrap", fontFamily: T.fontBody },
  wishlistBtn:      { position: "absolute", top: 6, right: 6, width: 24, height: 24, borderRadius: "50%", background: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer", fontSize: 11, boxShadow: T.shadowSm, zIndex: 2 },
  cardBody:         { padding: "8px 9px 9px", display: "flex", flexDirection: "column", flex: 1 },
  cardName:         { fontSize: 12.5, fontWeight: 600, color: T.black, margin: "0 0 2px", lineHeight: 1.3, minHeight: 32, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", fontFamily: T.fontBody },
  cardUnit:         { fontSize: 11, color: T.grey500, margin: "0 0 4px", fontFamily: T.fontBody },
  cardRatingRow:    { display: "flex", alignItems: "center", gap: 3, marginBottom: 4 },
  cardRatingStar:   { fontSize: 11, color: "#D4A017", fontWeight: 600 },
  cardRatingCount:  { fontSize: 11, color: T.grey500, fontFamily: T.fontBody },
  cardScarcity:     { fontSize: 11, color: T.red, fontWeight: 600, margin: "0 0 4px", fontFamily: T.fontBody },
  cardPrice:        { fontSize: 16, fontWeight: 700, color: T.black, margin: "0 0 6px", fontFamily: T.fontBody },
  cardUnitPrice:    { fontSize: 10, color: T.grey500, margin: "0 0 6px", fontFamily: T.fontBody },
  addBtnWrap:       { display: "flex", justifyContent: "flex-end" },
  addBtn:           { background: T.white, color: T.orange, border: `1.5px solid ${T.orange}`, borderRadius: 9, padding: "6px 14px", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.4px", cursor: "pointer", fontFamily: T.fontBody, transition: `all 0.2s ${T.ease}`, boxShadow: "0 2px 8px rgba(232,71,10,0.12)", flexShrink: 0 },
  addBtnDisabled:   { background: T.grey100, color: T.grey500, border: `1.5px solid ${T.grey300}`, borderRadius: 9, padding: "6px 10px", fontSize: 10.5, fontWeight: 700, cursor: "not-allowed", fontFamily: T.fontBody, flexShrink: 0 },
  qtyControl:       { display: "inline-flex", alignItems: "center", background: "linear-gradient(135deg, #E8470A 0%, #FF8A3D 100%)", borderRadius: 9, padding: "4px 6px", gap: 7, boxShadow: "0 4px 12px rgba(232,71,10,0.25)", flexShrink: 0 },
  qtyBtn:           { background: "transparent", border: "none", color: T.white, fontSize: 16, fontWeight: 700, cursor: "pointer", lineHeight: 1, padding: "0 2px" },
  qtyNum:           { color: T.white, fontWeight: 700, fontSize: 13, fontFamily: T.fontBody, minWidth: 14, textAlign: "center" },

  // Tab bar
  tabBar:  { position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 480, background: T.white, borderTop: `1px solid ${T.grey100}`, display: "flex", zIndex: 200, boxSizing: "border-box", height: 60, paddingBottom: "env(safe-area-inset-bottom)", boxShadow: "0 -2px 12px rgba(0,0,0,0.06)" },
  tabItem: (active) => ({ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 3, background: "transparent", border: "none", padding: "6px 0", color: active ? T.black : T.grey300 }),
  tabLabel: (active) => ({
    fontSize: 10, fontWeight: active ? 700 : 500, fontFamily: T.fontBody,
    color: active ? T.black : T.grey500,
  }),

  // Order bar
  mapFallback: {
    marginTop: 10, padding: "12px 14px", borderRadius: 14,
    background: "#F5EBD9", border: "1px solid #E8E0D0",
  },
  mapFallbackTitle: { margin: "0 0 3px", fontSize: 13, fontWeight: 700, color: "#1A0A00", fontFamily: T.fontBody },
  mapFallbackText:  { margin: 0, fontSize: 12, color: "#4A2E1A", fontFamily: T.fontBody, lineHeight: 1.4 },

  // Sits above the sticky order bar so it never covers the Place Order button.
  addedToast: {
    // Centred with auto margins, NOT translateX(-50%): this element carries the
    // fade-up animation, whose keyframes end at transform: translateY(0) and
    // would wipe out any centring transform, throwing the pill off-screen.
    position: "fixed", bottom: "calc(132px + env(safe-area-inset-bottom))",
    left: 16, right: 16, marginInline: "auto", zIndex: 240,
    display: "flex", alignItems: "center", gap: 8,
    width: "fit-content", maxWidth: 440, padding: "10px 16px",
    borderRadius: 100, background: "rgba(26,10,0,0.92)", color: "#FFFFFF",
    boxShadow: "0 8px 24px rgba(26,10,0,0.28)", pointerEvents: "none",
  },
  addedToastCheck: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 18, height: 18, borderRadius: "50%", background: "#1A6B3C",
    fontSize: 11, fontWeight: 700, flexShrink: 0,
  },
  addedToastText: {
    fontSize: 13, fontWeight: 500, fontFamily: T.fontBody,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },

  orderBarWrap: { position: "fixed", bottom: "calc(60px + env(safe-area-inset-bottom))", left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 480, padding: "8px 12px", boxSizing: "border-box", zIndex: 199 },
  orderBarBtn:  { width: "100%", background: T.black, color: T.white, border: "none", borderRadius: T.radiusLg, padding: "14px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: T.shadowLg, fontFamily: T.fontBody },

  // Bottom sheet
  overlay:     { position: "fixed", inset: 0, background: "rgba(26,10,0,0.4)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" },
  sheet:       { width: "100%", maxWidth: 480, background: T.white, borderRadius: `${T.radiusLg}px ${T.radiusLg}px 0 0`, padding: "0 20px 40px", boxSizing: "border-box", maxHeight: "88vh", overflowY: "auto" },
  sheetHandleBtn: { width: "100%", height: 28, border: "none", background: "transparent", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "grab" },
  sheetHandle: { display: "block", width: 36, height: 4, background: T.grey300, borderRadius: T.radiusPill },
  sheetHeader: { display: "grid", gridTemplateColumns: "64px 1fr 64px", alignItems: "center", gap: 8, margin: "0 0 16px" },
  sheetTextBtn: { background: "transparent", border: "none", color: T.orange, fontSize: 13, fontWeight: 700, fontFamily: T.fontBody, cursor: "pointer", padding: 0 },
  sheetTitle:  { fontFamily: T.fontDisplay, fontSize: 20, fontWeight: 700, color: T.black, margin: "0 0 16px" },

  // Bill
  billRow:     { display: "flex", justifyContent: "space-between", fontSize: 13, color: T.grey500, marginBottom: 6, fontFamily: T.fontBody },
  // Product detail sheet
  pdpImageWrap:  { position: "relative", background: "linear-gradient(180deg, #FAF7F4 0%, #FFFFFF 100%)", borderRadius: T.radiusMd, overflow: "hidden", marginBottom: 14 },
  pdpImage:      { width: "100%", height: 200, objectFit: "contain", display: "block", mixBlendMode: "multiply" },
  pdpCategory:   { fontSize: 11, fontWeight: 800, color: T.orange, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: T.fontBody, margin: "0 0 5px" },
  pdpName:       { fontSize: 20, fontWeight: 700, color: T.black, fontFamily: T.fontDisplay, margin: "0 0 14px", lineHeight: 1.25 },
  pdpFactRow:    { display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" },
  pdpFact:       { flex: "1 1 90px", background: T.grey50 || "#F5EBD9", border: `1px solid ${T.grey100}`, borderRadius: 10, padding: "9px 10px", display: "flex", flexDirection: "column", gap: 2 },
  pdpFactLabel:  { fontSize: 10, color: T.grey500, fontFamily: T.fontBody, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" },
  pdpFactValue:  { fontSize: 13, color: T.black, fontFamily: T.fontBody, fontWeight: 700 },
  pdpPriceRow:   { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, paddingTop: 14, borderTop: `1px solid ${T.grey100}` },
  pdpPrice:      { fontSize: 22, fontWeight: 700, fontFamily: T.fontBody, lineHeight: 1.1 },
  pdpAddBtn:     { background: "linear-gradient(135deg, #E8470A 0%, #FF8A3D 100%)", color: T.white, border: "none", borderRadius: 12, padding: "12px 26px", fontSize: 14, fontWeight: 700, fontFamily: T.fontBody, cursor: "pointer", boxShadow: "0 6px 18px rgba(232,71,10,0.28)", flexShrink: 0 },

  searchSuggestHeading: { fontSize: 11, fontWeight: 800, color: T.grey500, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: T.fontBody, margin: "0 0 10px" },
  searchSuggestWrap:    { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 22 },
  searchSuggestChip:    { background: T.white, border: `1px solid ${T.grey100}`, borderRadius: 100, padding: "8px 14px", fontSize: 12.5, fontWeight: 600, color: T.black, fontFamily: T.fontBody, cursor: "pointer", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },

  savingsPill: { marginTop: 10, background: "#E8F5EE", color: "#1A6B3C", borderRadius: 10, padding: "8px 12px", fontSize: 12.5, fontWeight: 700, fontFamily: T.fontBody, textAlign: "center" },
  billDivider: { borderTop: `1px solid ${T.grey100}`, margin: "8px 0" },
  billTotal:   { display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 700, color: T.black, fontFamily: T.fontBody },

  // Modal item row
  modalItemRow:      { display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${T.grey100}` },
  modalItemImg:      { width: 44, height: 44, objectFit: "contain", borderRadius: T.radiusSm, background: T.grey100, flexShrink: 0 },
  modalItemInfo:     { flex: 1, minWidth: 0 },
  modalItemName:     { fontSize: 13, fontWeight: 500, color: T.black, margin: "0 0 2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: T.fontBody },
  modalItemSub:      { fontSize: 11, color: T.grey500, margin: 0, fontFamily: T.fontBody },
  modalItemRight:    { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 },
  modalItemSubtotal: { fontSize: 13, fontWeight: 700, color: T.black, fontFamily: T.fontBody },

  // Form
  formGroup:     { marginBottom: 16 },
  formLabel:     { display: "block", fontSize: 11, fontWeight: 600, color: T.grey500, marginBottom: 6, fontFamily: T.fontBody, textTransform: "uppercase", letterSpacing: "0.5px" },
  formInput:     { width: "100%", padding: "10px 0", fontSize: 15, border: "none", borderBottom: `1.5px solid ${T.grey300}`, outline: "none", background: "transparent", color: T.black, fontFamily: T.fontBody, boxSizing: "border-box", transition: `border-color 0.2s ${T.ease}` },
  locationRow:   { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 10 },
  locationBtn:   { flexShrink: 0, background: T.offWhite, color: T.black, border: `1px solid ${T.grey100}`, borderRadius: T.radiusPill, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody },
  locationHint:  { margin: 0, color: T.grey500, fontSize: 11, lineHeight: 1.35, fontFamily: T.fontBody },
  mapPreview:    { width: "100%", height: 150, borderRadius: T.radiusMd, overflow: "hidden", background: T.grey100, border: `1px solid ${T.grey100}`, marginTop: 10 },
  mapLoading:    { height: 150, borderRadius: T.radiusMd, background: T.grey100, color: T.grey500, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontFamily: T.fontBody, marginTop: 10 },
  pinStatus:     { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 8 },
  placeOrderBtn: { width: "100%", background: T.black, color: T.white, border: "none", borderRadius: T.radiusPill, height: 52, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody, marginTop: 16, transition: `opacity 0.2s ${T.ease}`, display: "flex", alignItems: "center", justifyContent: "center" },
  deliveryConfirmCard: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, background: T.offWhite, borderRadius: T.radiusMd, padding: "12px 14px", margin: "12px 0 4px" },
  deliveryConfirmTitle: { margin: "0 0 5px", color: T.black, fontSize: 13, fontWeight: 700, fontFamily: T.fontBody },
  deliveryConfirmPrimary: { margin: "0 0 3px", color: T.black, fontSize: 13, fontWeight: 700, fontFamily: T.fontBody, lineHeight: 1.35 },
  deliveryConfirmText: { margin: 0, color: T.grey500, fontSize: 12, lineHeight: 1.45, fontFamily: T.fontBody },
  deliveryEditBtn: { flexShrink: 0, background: "transparent", border: "none", color: T.orange, fontSize: 12, fontWeight: 700, fontFamily: T.fontBody, cursor: "pointer", padding: 0 },

  // Success
  successOverlay:     { position: "fixed", inset: 0, background: T.white, zIndex: 400, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, boxSizing: "border-box" },
  successCheckCircle: { width: 80, height: 80, borderRadius: "50%", background: "#E8F5EE", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 24 },
  successTitle:       { fontFamily: T.fontDisplay, fontSize: 28, fontWeight: 700, color: T.black, margin: "0 0 8px", textAlign: "center" },
  successSubtitle:    { fontFamily: T.fontBody, fontSize: 14, color: T.grey500, textAlign: "center", margin: "0 0 32px", lineHeight: 1.6 },
  successBtn:         { width: "100%", maxWidth: 340, background: T.black, color: T.white, border: "none", borderRadius: T.radiusPill, height: 52, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody },

  // Search tab
  searchTabWrap:       { flex: 1, display: "flex", flexDirection: "column", padding: "12px 12px" },
  searchResultRow:     { display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.grey100}` },
  searchResultImg:     { width: 52, height: 52, objectFit: "contain", borderRadius: T.radiusSm, background: T.grey100, flexShrink: 0 },
  searchResultInfo:    { flex: 1, minWidth: 0 },
  searchResultName:    { fontSize: 13, fontWeight: 500, color: T.black, margin: "0 0 2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: T.fontBody },
  searchResultSub:     { fontSize: 11, color: T.grey500, margin: 0, fontFamily: T.fontBody },
  searchResultPrice:   { fontSize: 14, fontWeight: 700, color: T.black, flexShrink: 0, fontFamily: T.fontBody },

  // Cart tab
  cartTabWrap:    { flex: 1, display: "flex", flexDirection: "column", padding: "16px 12px" },
  cartRow:        { display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.grey100}` },
  cartRowImg:     { width: 52, height: 52, objectFit: "contain", borderRadius: T.radiusSm, background: T.grey100, flexShrink: 0 },
  cartRowInfo:    { flex: 1, minWidth: 0 },
  cartRowName:    { fontSize: 13, fontWeight: 500, color: T.black, margin: "0 0 4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: T.fontBody },
  cartRowTotal:   { fontSize: 13, fontWeight: 700, color: T.black, flexShrink: 0, fontFamily: T.fontBody },
  emptyCartWrap:  { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 32 },
  emptyCartTitle: { fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 700, color: T.black, margin: 0 },
  emptyCartSub:   { fontSize: 14, color: T.grey500, margin: 0, textAlign: "center", fontFamily: T.fontBody },
  shopNowBtn:     { background: T.black, color: T.white, border: "none", borderRadius: T.radiusPill, padding: "12px 28px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: T.fontBody, marginTop: 8 },

  // Categories tab
  catTabWrap:         { flex: 1, display: "flex", flexDirection: "column", padding: "16px 0" },
  catTabRow:          { display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: `1px solid ${T.grey100}`, cursor: "pointer", background: T.white },
  catTabRowImg:       { width: 52, height: 52, borderRadius: T.radiusSm, objectFit: "cover", background: T.grey100, flexShrink: 0 },
  catTabRowInfo:      { flex: 1, minWidth: 0 },
  catTabRowName:      { fontSize: 14, fontWeight: 600, color: T.black, margin: "0 0 2px", fontFamily: T.fontBody },
  catTabRowCount:     { fontSize: 12, color: T.grey500, margin: 0, fontFamily: T.fontBody },
  catTabRowChevron:   { fontSize: 18, color: T.grey300 },

  // Spinner / skeleton
  spinner:          { width: 28, height: 28, border: `2.5px solid ${T.grey100}`, borderTop: `2.5px solid ${T.orange}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "60px auto" },
  centered:         { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, gap: 16, color: T.grey500, fontSize: 14, fontFamily: T.fontBody },
  skeletonGrid:     { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "0 12px" },
  skeletonCardWrap: { background: T.white, borderRadius: T.radiusMd, boxShadow: T.shadowSm, overflow: "hidden" },
  skeletonCardImg:  { width: "100%", height: 120, background: T.grey100 },
  skeletonLine:     (w) => ({ height: 10, borderRadius: T.radiusSm, background: T.grey100, margin: "6px 10px", width: w || "70%" }),
};

// ─── SkeletonCard ─────────────────────────────────────────────────────────────
// `animate` is off for placeholders that sit below the fold: a long catalogue
// can hold a few hundred of these, and animating skeletons the customer cannot
// see is pure paint cost on low-end phones.
function SkeletonCard({ animate = true }) {
  const cls = animate ? "shimmer" : undefined;
  return (
    <div style={S.skeletonCardWrap}>
      <div style={S.skeletonCardImg} className={cls} />
      <div style={S.skeletonLine("80%")} className={cls} />
      <div style={S.skeletonLine("55%")} className={cls} />
      <div style={{ ...S.skeletonLine("40%"), marginBottom: 10 }} className={cls} />
    </div>
  );
}

// ─── SmartImage ───────────────────────────────────────────────────────────────
// Product/category images arrive over the network well after the surrounding
// card has painted, which used to leave an empty tinted tile sitting there.
// This keeps a shimmer over the slot until the image actually decodes, then
// fades it in. `fallback` renders instead of the shimmer once onError fires.
// Cached images fire onLoad before effects run, so we check `complete` too.
function SmartImage({ src, alt, style, wrapStyle, fallback = null, onError, ...rest }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [near, setNear] = useState(false);
  const imgRef = useRef(null);
  const wrapRef = useRef(null);

  // Reset when the source changes (e.g. a card is recycled for another product).
  useEffect(() => { setLoaded(false); setFailed(false); }, [src]);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  // Images are lazy, so an offscreen one never loads and would otherwise keep a
  // shimmer animating forever. A long catalogue can hold hundreds of these at
  // once, which is real paint work on the low-end phones this is meant to help.
  // Only animate the placeholder once the slot is near the viewport.
  useEffect(() => {
    if (loaded || typeof IntersectionObserver === "undefined") { setNear(true); return undefined; }
    const node = wrapRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setNear(true); observer.disconnect(); } },
      { rootMargin: "300px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loaded]);

  if (!src || failed) return fallback;

  return (
    <span ref={wrapRef} style={{ position: "relative", display: "block", width: "100%", height: "100%", ...wrapStyle }}>
      {!loaded && (
        <span
          className={near ? "shimmer" : undefined}
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0, display: "block", borderRadius: "inherit",
            zIndex: 1, pointerEvents: "none",
            ...(near ? null : { background: "#F3ECE0" }),
          }}
        />
      )}
      {/* The image is never hidden by us: the placeholder covers it until the
          pixels arrive. Fading the image in instead would mean an image that
          stays invisible whenever a transition does not run to completion
          (background tab, reduced-motion, interrupted paint). */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={(e) => { setFailed(true); if (onError) onError(e); }}
        style={style}
        {...rest}
      />
    </span>
  );
}

// ─── LazySection ──────────────────────────────────────────────────────────────
function LazySection({ eager, children }) {
  const [visible, setVisible] = useState(!!eager);
  const ref = useRef(null);
  useEffect(() => {
    if (eager || !ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { rootMargin: "400px" }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [eager]);
  if (visible) return children;
  return (
    <div ref={ref}>
      <div style={S.skeletonGrid}>
        {/* Static: this placeholder only exists while the section is off-screen. */}
        {[0,1,2,3].map((i) => <SkeletonCard key={i} animate={false} />)}
      </div>
    </div>
  );
}

// Category-tile image preference: the tile picks the first product image in the
// category, but some pack shots sit on a photographic (non-white) background and
// break the clean-tile look. For those categories, prefer a product known to have
// a white-background pack shot; fall back to the first image as before.
const CATEGORY_CARD_IMAGE_PREF = {
  "Dairy & Bakery": /amul.*(butter|taaza|cheese|paneer)|butter|paneer|milk/i,
};

function pickCategoryCardImage(cat, items) {
  const pref = CATEGORY_CARD_IMAGE_PREF[cat];
  if (pref) {
    const hit = items.find((p) => p.image && pref.test(p.name || ""));
    if (hit) return hit.image;
  }
  return items.find((p) => p.image)?.image || CATEGORY_FALLBACK_IMAGE[cat] || null;
}

// ─── CategoryGridCard ─────────────────────────────────────────────────────────
function CategoryGridCard({ cat, items, onTap }) {
  const imgSrc  = pickCategoryCardImage(cat, items);
  const [failed,  setFailed]  = useState(false);
  const [pressed, setPressed] = useState(false);
  const src = (!failed && imgSrc) ? imgSrc : null;
  return (
    <button
      style={{ ...S.catCard, transform: pressed ? "scale(0.97)" : "scale(1)" }}
      type="button"
      onClick={onTap}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
    >
      <div style={S.catCardImgWrap}>
        <SmartImage
          src={src}
          alt={cat}
          style={S.catCardImg}
          onError={() => setFailed(true)}
          fallback={<div style={S.catCardImgFallback}>📦</div>}
        />
        <div style={S.catCardGradient} />
      </div>
      <div style={S.catCardBody}>
        <span style={S.catCardName}>{cat}</span>
      </div>
    </button>
  );
}

// ─── FeaturedCard ─────────────────────────────────────────────────────────────
function FeaturedCard({ product, onTap }) {
  const [failed, setFailed] = useState(false);
  const src = (!failed && product.image) ? product.image : CATEGORY_FALLBACK_IMAGE[getDisplayCategory(product.category)];
  const discount = normalizeDiscountPercent(product.discount);
  return (
    <div style={S.featuredCard} onClick={onTap}>
      <SmartImage
        src={src}
        alt={product.name}
        style={S.featuredImg}
        wrapStyle={{ height: 100, flexShrink: 0 }}
        onError={() => setFailed(true)}
      />
      <div style={S.featuredBody}>
        <p style={S.featuredName}>{product.name}</p>
        {discount > 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: T.grey500, textDecoration: "line-through", fontFamily: T.fontBody }}>₹{getOriginalUnitPrice(product)}</span>
            <span style={{ ...S.featuredPrice, color: T.green, margin: 0 }}>₹{getEffectiveUnitPrice(product).toFixed(2).replace(/\.00$/, "")}</span>
            <span style={{ fontSize: 10, background: T.green, color: T.white, borderRadius: 4, padding: "1px 5px", fontFamily: T.fontBody, fontWeight: 600 }}>{discount}% off</span>
          </div>
        ) : (
          <p style={S.featuredPrice}>₹{product.price}</p>
        )}
      </div>
    </div>
  );
}

// ─── ProductCard ──────────────────────────────────────────────────────────────
function ProductCard({ product, quantity, onUpdate, isPopular, wishlisted, onWishlist, onOpen }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [pressed, setPressed]     = useState(false);
  const imageSrc = (!imgFailed && product.image)
    ? product.image
    : (CATEGORY_FALLBACK_IMAGE[getDisplayCategory(product.category)] || null);
  const isOOS           = product.stock === 0;
  const priceStr        = String(product.price);
  const isSale          = priceStr.endsWith(".1") || priceStr.endsWith(".85");
  const showScarcity    = product.stock > 0 && product.stock < 5;
  const unitPrice       = calcUnitPrice(product.price, product.unit);
  const discountPct     = normalizeDiscountPercent(product.discount);
  const hasDiscount     = discountPct > 0;
  const discountedPrice = hasDiscount ? getEffectiveUnitPrice(product).toFixed(2).replace(/\.00$/, "") : null;

  return (
    <div
      style={{ ...S.card, transform: pressed ? "scale(0.97)" : "scale(1)" }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
    >
      {/* Image and title open the detail sheet. The footer row is left alone so
          ADD and the quantity stepper stay one-tap actions. */}
      <div style={S.cardImgWrap} onClick={onOpen} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen && onOpen(); } }}
        aria-label={`View details for ${product.name}`}
      >
        <SmartImage
          src={imageSrc}
          alt={product.name}
          style={{ ...S.cardImg, opacity: isOOS ? 0.4 : 1 }}
          wrapStyle={{ height: 120, flexShrink: 0 }}
          onError={() => setImgFailed(true)}
          fallback={<div style={{ ...S.cardImgFallback, opacity: isOOS ? 0.4 : 1 }}>{product.name.charAt(0)}</div>}
        />
        {hasDiscount && !isOOS && <span style={S.cardDiscountFlag}>{discountPct}% OFF</span>}
        {!hasDiscount && !isSale && isPopular && !isOOS && <span style={S.popularBadge}>Popular</span>}
        {isOOS && <span style={S.oosOverlay}>Out of Stock</span>}
        <button
          style={S.wishlistBtn}
          type="button"
          onClick={(e) => { e.stopPropagation(); onWishlist(product.id); }}
          aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
        >
          {wishlisted ? "❤️" : "♡"}
        </button>
      </div>
      <div style={S.cardBody}>
        <p style={S.cardName} onClick={onOpen}>{product.name}</p>
        {/* Pack size and per-100g price share one line instead of two rows. */}
        <p style={S.cardMetaRow}>{[product.unit, unitPrice].filter(Boolean).join(" · ")}</p>
        {showScarcity && <p style={S.cardScarcity}>Only {product.stock} left!</p>}
        <div style={S.cardFooterRow}>
          <div style={{ minWidth: 0 }}>
            {hasDiscount && (
              <span style={S.cardPriceWas}>₹{getOriginalUnitPrice(product)}</span>
            )}
            <span style={{ ...S.cardPriceNow, color: hasDiscount ? T.green : T.black }}>
              ₹{hasDiscount ? discountedPrice : product.price}
            </span>
          </div>
          {isOOS ? (
            <button style={S.addBtnDisabled} type="button" disabled>Sold out</button>
          ) : quantity === 0 ? (
            <button style={S.addBtn} type="button" onClick={() => onUpdate(product.id, 1)}>ADD</button>
          ) : (
            <div style={S.qtyControl}>
              <button style={S.qtyBtn} type="button" aria-label={`Remove one ${product.name}`} onClick={() => onUpdate(product.id, -1)}>−</button>
              <span style={S.qtyNum}>{quantity}</span>
              <button style={S.qtyBtn} type="button" aria-label={`Add one ${product.name}`} onClick={() => onUpdate(product.id, 1)}>+</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ProductDetailSheet ───────────────────────────────────────────────────────
// Built entirely from fields the merchant already fills in when adding a product
// (name, unit, price, discount, stock, category, image). There is deliberately
// no description field: asking every merchant to write copy for every product
// is work they will not do, and empty descriptions look worse than none. Depth
// comes from what we can derive instead: pack size, price per 100g, stock, and
// related products from the same catalogue.
function ProductDetailSheet({ product, quantity, onUpdate, onClose, related, onOpenRelated, wishlisted, onWishlist }) {
  const [imgFailed, setImgFailed] = useState(false);
  const sheetRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    sheetRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus?.();
    };
  }, [onClose]);

  if (!product) return null;

  const src = (!imgFailed && product.image)
    ? product.image
    : (CATEGORY_FALLBACK_IMAGE[getDisplayCategory(product.category)] || null);
  const isOOS       = product.stock === 0;
  const discountPct = normalizeDiscountPercent(product.discount);
  const hasDiscount = discountPct > 0;
  const nowPrice    = hasDiscount ? getEffectiveUnitPrice(product).toFixed(2).replace(/\.00$/, "") : product.price;
  const unitPrice   = calcUnitPrice(getEffectiveUnitPrice(product), product.unit);

  return (
    <div style={S.overlay} onClick={onClose}>
      <div
        ref={sheetRef}
        style={S.sheet}
        className="slide-up"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-detail-title"
        tabIndex={-1}
      >
        <button style={S.sheetHandleBtn} type="button" aria-label="Close product details" onClick={onClose}>
          <span style={S.sheetHandle} />
        </button>

        <div style={S.pdpImageWrap}>
          <SmartImage
            src={src}
            alt={product.name}
            style={S.pdpImage}
            wrapStyle={{ height: 200 }}
            onError={() => setImgFailed(true)}
            fallback={<div style={{ ...S.pdpImage, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 44, color: T.grey300 }}>{product.name.charAt(0)}</div>}
          />
          {hasDiscount && !isOOS && <span style={S.cardDiscountFlag}>{discountPct}% OFF</span>}
          <button
            style={S.wishlistBtn}
            type="button"
            onClick={() => onWishlist(product.id)}
            aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
          >
            {wishlisted ? "❤️" : "♡"}
          </button>
        </div>

        <p style={S.pdpCategory}>{getDisplayCategory(product.category)}</p>
        <h2 id="product-detail-title" style={S.pdpName}>{product.name}</h2>

        <div style={S.pdpFactRow}>
          <div style={S.pdpFact}>
            <span style={S.pdpFactLabel}>Pack size</span>
            <span style={S.pdpFactValue}>{product.unit || "1 unit"}</span>
          </div>
          {unitPrice && (
            <div style={S.pdpFact}>
              <span style={S.pdpFactLabel}>Unit price</span>
              <span style={S.pdpFactValue}>{unitPrice}</span>
            </div>
          )}
          <div style={S.pdpFact}>
            <span style={S.pdpFactLabel}>Availability</span>
            <span style={{ ...S.pdpFactValue, color: isOOS ? T.red : T.green }}>
              {isOOS ? "Out of stock" : product.stock > 0 && product.stock < 5 ? `Only ${product.stock} left` : "In stock"}
            </span>
          </div>
        </div>

        <div style={S.pdpPriceRow}>
          <div>
            {hasDiscount && <span style={S.cardPriceWas}>₹{getOriginalUnitPrice(product)}</span>}
            <span style={{ ...S.pdpPrice, color: hasDiscount ? T.green : T.black }}>₹{nowPrice}</span>
          </div>
          {isOOS ? (
            <button style={S.addBtnDisabled} type="button" disabled>Sold out</button>
          ) : quantity === 0 ? (
            <button style={S.pdpAddBtn} type="button" onClick={() => onUpdate(product.id, 1)}>Add to cart</button>
          ) : (
            <div style={S.qtyControl}>
              <button style={S.qtyBtn} type="button" aria-label="Remove one" onClick={() => onUpdate(product.id, -1)}>−</button>
              <span style={S.qtyNum}>{quantity}</span>
              <button style={S.qtyBtn} type="button" aria-label="Add one" onClick={() => onUpdate(product.id, 1)}>+</button>
            </div>
          )}
        </div>

        {related.length > 0 && (
          <>
            <p style={{ ...S.featuredTitle, margin: "22px 0 10px" }}>More in {getDisplayCategory(product.category)}</p>
            <div style={{ ...S.featuredScroll, paddingLeft: 0 }}>
              {related.map((p) => (
                <FeaturedCard key={p.id} product={p} onTap={() => onOpenRelated(p)} />
              ))}
              <div style={{ minWidth: 8, flexShrink: 0 }} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── SearchResultRow ──────────────────────────────────────────────────────────
function SearchResultRow({ product, quantity, onUpdate, onOpen }) {
  const [imgFailed, setImgFailed] = useState(false);
  const src   = (!imgFailed && product.image) ? product.image : CATEGORY_FALLBACK_IMAGE[getDisplayCategory(product.category)];
  const isOOS = product.stock === 0;
  return (
    <div style={S.searchResultRow}>
      <SmartImage
        src={src}
        alt={product.name}
        style={{ ...S.searchResultImg, opacity: isOOS ? 0.4 : 1 }}
        wrapStyle={{ width: 52, height: 52, flexShrink: 0, borderRadius: T.radiusSm }}
        onError={() => setImgFailed(true)}
        fallback={<div style={{ ...S.searchResultImg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: T.grey300 }}>{product.name.charAt(0)}</div>}
      />
      <div style={S.searchResultInfo}>
        <p style={{ ...S.searchResultName, cursor: onOpen ? "pointer" : undefined }} onClick={onOpen}>{product.name}</p>
        <p style={S.searchResultSub}>{product.unit} · {product.category}</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
        {(() => {
          const discountPct = normalizeDiscountPercent(product.discount);
          const hasDiscount = discountPct > 0;
          const discountedPrice = hasDiscount
            ? getEffectiveUnitPrice(product).toFixed(2).replace(/\.00$/, "")
            : null;
          return hasDiscount ? (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 12, color: T.grey500, textDecoration: "line-through", fontFamily: T.fontBody }}>₹{getOriginalUnitPrice(product)}</span>
              <span style={{ ...S.searchResultPrice, color: T.green }}>₹{discountedPrice}</span>
              <span style={{ fontSize: 10, background: T.green, color: T.white, borderRadius: 4, padding: "1px 5px", fontFamily: T.fontBody, fontWeight: 600 }}>{discountPct}% off</span>
            </div>
          ) : (
            <span style={S.searchResultPrice}>₹{product.price}</span>
          );
        })()}
        {isOOS ? (
          <button style={{ ...S.addBtnDisabled, padding: "4px 10px", fontSize: 11 }} disabled>Out of Stock</button>
        ) : quantity === 0 ? (
          <button style={S.addBtn} type="button" onClick={() => onUpdate(product.id, 1)}>ADD</button>
        ) : (
          <div style={S.qtyControl}>
            <button style={S.qtyBtn} type="button" onClick={() => onUpdate(product.id, -1)}>−</button>
            <span style={S.qtyNum}>{quantity}</span>
            <button style={S.qtyBtn} type="button" onClick={() => onUpdate(product.id, 1)}>+</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CartRow ──────────────────────────────────────────────────────────────────
function CartRow({ item, onUpdate }) {
  const [imgFailed, setImgFailed] = useState(false);
  const src = (!imgFailed && item.image) ? item.image : CATEGORY_FALLBACK_IMAGE[getDisplayCategory(item.category)];
  return (
    <div style={S.cartRow}>
      <SmartImage
        src={src}
        alt={item.name}
        style={S.cartRowImg}
        wrapStyle={{ width: 52, height: 52, flexShrink: 0, borderRadius: T.radiusSm }}
        onError={() => setImgFailed(true)}
      />
      <div style={S.cartRowInfo}>
        <p style={S.cartRowName}>{item.name}</p>
        <div style={S.qtyControl}>
          <button style={S.qtyBtn} type="button" onClick={() => onUpdate(item.id, -1)}>−</button>
          <span style={S.qtyNum}>{item.quantity}</span>
          <button style={S.qtyBtn} type="button" onClick={() => onUpdate(item.id, 1)}>+</button>
        </div>
      </div>
      <span style={S.cartRowTotal}>₹{getLineTotal(item).toFixed(2).replace(/\.00$/, "")}</span>
    </div>
  );
}

// ─── ModalCartItemRow ─────────────────────────────────────────────────────────
function ModalCartItemRow({ item, onUpdate }) {
  const [imgFailed, setImgFailed] = useState(false);
  const src = (!imgFailed && item.image) ? item.image : CATEGORY_FALLBACK_IMAGE[getDisplayCategory(item.category)];
  return (
    <div style={S.modalItemRow}>
      <SmartImage
        src={src}
        alt={item.name}
        style={S.modalItemImg}
        wrapStyle={{ width: 44, height: 44, flexShrink: 0, borderRadius: T.radiusSm }}
        onError={() => setImgFailed(true)}
      />
      <div style={S.modalItemInfo}>
        <p style={S.modalItemName}>{item.name}</p>
        <p style={S.modalItemSub}>{item.unit}</p>
      </div>
      <div style={S.modalItemRight}>
        <span style={S.modalItemSubtotal}>₹{getLineTotal(item).toFixed(2).replace(/\.00$/, "")}</span>
        <div style={S.qtyControl}>
          <button style={S.qtyBtn} type="button" onClick={() => onUpdate(item.id, -1)}>−</button>
          <span style={S.qtyNum}>{item.quantity}</span>
          <button style={S.qtyBtn} type="button" onClick={() => onUpdate(item.id, 1)}>+</button>
        </div>
      </div>
    </div>
  );
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const IconReceipt = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16l3-2 3 2 3-2 3 2 3-2V4a2 2 0 0 0-3-2z"/>
    <line x1="9" y1="9" x2="15" y2="9"/>
    <line x1="9" y1="13" x2="15" y2="13"/>
  </svg>
);
const IconHome = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const IconSearch = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const IconBag = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 0 1-8 0"/>
  </svg>
);
const IconGrid = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
  </svg>
);
const IconProfile = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

// ─── LoginScreen ──────────────────────────────────────────────────────────────
function LoginScreen({ storeName, loginPhone, setLoginPhone, loginOtp, setLoginOtp, loginStep, loginLoading, loginError, onSendOtp, onVerifyOtp, onBack }) {
  const [agreed, setAgreed] = React.useState(false);
  const loginCard = {
    width: "100%", maxWidth: 360, background: "rgba(255,255,255,0.92)",
    borderRadius: 28, padding: "28px 24px",
    boxShadow: "0 24px 60px rgba(232,71,10,0.10), 0 2px 8px rgba(26,10,0,0.04)",
    border: "1px solid rgba(255,255,255,0.9)",
    backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
    boxSizing: "border-box", position: "relative", zIndex: 1,
  };
  const loginBtn = {
    ...S.placeOrderBtn,
    background: "linear-gradient(135deg, #E8470A 0%, #FF8A3D 100%)",
    boxShadow: "0 10px 24px rgba(232,71,10,0.30)",
  };
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 24px", minHeight: "100vh", boxSizing: "border-box", position: "relative", background: "linear-gradient(180deg, #FFF6F0 0%, #FFFDFB 45%, #FFF9F4 100%)", overflow: "hidden" }}>
      {/* soft saffron glow */}
      <div style={{ position: "absolute", top: -140, right: -120, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,138,61,0.22) 0%, rgba(255,138,61,0) 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -160, left: -140, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(232,71,10,0.10) 0%, rgba(232,71,10,0) 70%)", pointerEvents: "none" }} />
      {loginStep === "phone" ? (
        <>
          <div style={{ width: 84, height: 84, background: "linear-gradient(135deg, #E8470A 0%, #FF8A3D 100%)", borderRadius: 28, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18, boxShadow: "0 14px 32px rgba(232,71,10,0.28)", position: "relative", zIndex: 1 }}>
            <span style={{ fontSize: 36, fontFamily: T.fontDisplay, fontWeight: 700, color: T.white }}>{storeName[0]}</span>
          </div>
          <p style={{ fontFamily: T.fontDisplay, fontSize: 28, fontWeight: 700, color: T.black, textAlign: "center", margin: "0 0 4px", position: "relative", zIndex: 1 }}>
            {storeName}
          </p>
          <p style={{ fontFamily: T.fontBody, fontSize: 13, color: T.grey500, textAlign: "center", margin: "0 0 28px", position: "relative", zIndex: 1 }}>
            Fresh · Local · Fast
          </p>
          <div style={loginCard}>
            <label style={{ fontFamily: T.fontBody, fontSize: 11, fontWeight: 700, color: T.grey500, letterSpacing: "0.8px", margin: "0 0 8px", display: "block" }}>MOBILE NUMBER</label>
            <div style={{ display: "flex", alignItems: "center", border: "1.5px solid #F0E4DC", borderRadius: 16, background: "#FDF6EC", padding: "0 16px", height: 54, overflow: "hidden", marginBottom: 16 }}>
              <span style={{ fontFamily: T.fontBody, fontSize: 15, fontWeight: 600, color: T.black, marginRight: 8, flexShrink: 0 }}>+91</span>
              <div style={{ width: 1, height: 20, background: "#E8DED6", marginRight: 12 }} />
              <input
                style={{ border: "none", outline: "none", background: "transparent", flex: 1, fontSize: 16, fontWeight: 500, fontFamily: T.fontBody, color: T.black, letterSpacing: "0.5px" }}
                type="tel"
                placeholder="98765 43210"
                value={loginPhone}
                maxLength={10}
                onChange={(e) => setLoginPhone(e.target.value.replace(/\D/g, ""))}
              />
            </div>
            {loginError && <p style={{ color: T.red, fontSize: 13, margin: "0 0 12px", fontFamily: T.fontBody }}>{loginError}</p>}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16, cursor: "pointer" }}>
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 2, accentColor: T.orange, width: 16, height: 16, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: T.grey500, fontFamily: T.fontBody, lineHeight: 1.6 }}>
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: T.orange, textDecoration: "none", fontWeight: 600 }}>Terms of Use</a>
                {" "}and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: T.orange, textDecoration: "none", fontWeight: 600 }}>Privacy Policy</a>
              </span>
            </label>
            <button
              style={{ ...loginBtn, opacity: (loginLoading || !agreed) ? 0.6 : 1 }}
              type="button"
              disabled={loginLoading || !agreed}
              onClick={onSendOtp}
            >
              {loginLoading
                ? <div style={{ width: 20, height: 20, border: `2px solid rgba(255,255,255,0.3)`, borderTop: `2px solid ${T.white}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                : "Continue →"
              }
            </button>
          </div>
          <a href="https://kiranaos.in/how-it-works" target="_blank" rel="noopener noreferrer" style={{ marginTop: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textDecoration: "none", position: "relative", zIndex: 1 }}>
            <span style={{ fontSize: 10, color: T.grey300, fontFamily: T.fontBody, letterSpacing: "1.2px", textTransform: "uppercase" }}>Powered by</span>
            <img src="/kiranaos-wordmark.png" alt="KiranaOS" style={{ height: 22, display: "block" }} />
          </a>
        </>
      ) : (
        <>
          <div style={loginCard}>
            <button
              type="button"
              onClick={onBack}
              style={{ background: "transparent", border: "none", color: T.grey500, fontSize: 13, fontFamily: T.fontBody, cursor: "pointer", marginBottom: 20, display: "block", padding: 0 }}
            >
              ← Change number
            </button>
            <p style={{ fontFamily: T.fontDisplay, fontSize: 28, fontWeight: 700, color: T.black, margin: "0 0 8px", textAlign: "center" }}>Enter OTP</p>
            <p style={{ fontFamily: T.fontBody, fontSize: 14, color: T.grey500, margin: "0 0 24px", textAlign: "center" }}>
              Sent to +91 {loginPhone} on WhatsApp
            </p>
            <input
              style={{ ...S.formInput, fontSize: 32, fontWeight: 700, textAlign: "center", letterSpacing: "12px", marginBottom: 16, border: "1.5px solid #F0E4DC", borderRadius: 16, background: "#FDF6EC", height: 68, boxSizing: "border-box" }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="••••"
              value={loginOtp}
              maxLength={4}
              onChange={(e) => setLoginOtp(e.target.value.slice(0, 4))}
            />
            {loginError && <p style={{ color: T.red, fontSize: 13, margin: "0 0 12px", fontFamily: T.fontBody }}>{loginError}</p>}
            <button
              style={{ ...loginBtn, opacity: loginLoading ? 0.6 : 1 }}
              type="button"
              disabled={loginLoading}
              onClick={onVerifyOtp}
            >
              {loginLoading
                ? <div style={{ width: 20, height: 20, border: `2px solid rgba(255,255,255,0.3)`, borderTop: `2px solid ${T.white}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                : "Verify OTP"
              }
            </button>
            <button
              type="button"
              onClick={onSendOtp}
              style={{ width: "100%", background: "transparent", border: "none", color: T.orange, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.fontBody, marginTop: 14 }}
            >
              Resend OTP
            </button>
          </div>
          <a href="https://kiranaos.in/how-it-works" target="_blank" rel="noopener noreferrer" style={{ marginTop: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textDecoration: "none", position: "relative", zIndex: 1 }}>
            <span style={{ fontSize: 10, color: T.grey300, fontFamily: T.fontBody, letterSpacing: "1.2px", textTransform: "uppercase" }}>Powered by</span>
            <img src="/kiranaos-wordmark.png" alt="KiranaOS" style={{ height: 22, display: "block" }} />
          </a>
        </>
      )}
    </div>
  );
}

// ─── BrandFooter ──────────────────────────────────────────────────────────────
function BrandFooter() {
  return (
    <a
      href="https://kiranaos.in/how-it-works"
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "10px 0 26px", textDecoration: "none" }}
    >
      <span style={{ fontSize: 10, color: T.grey300, fontFamily: T.fontBody, letterSpacing: "1.2px", textTransform: "uppercase" }}>Powered by</span>
      <img src="/kiranaos-wordmark.png" alt="KiranaOS" style={{ height: 18, display: "block", opacity: 0.85 }} />
    </a>
  );
}

// ─── ActivityBar ──────────────────────────────────────────────────────────────
function ActivityBar({ status, lang = "en", onTap }) {
  const STATUS_MSG = {
    pending:          { icon: "🟡", text: cc(lang, "statusPlaced") },
    accepted:         { icon: "📦", text: cc(lang, "statusPacked") },
    preparing:        { icon: "📦", text: cc(lang, "statusPacked") },
    out_for_delivery: { icon: "🚴", text: cc(lang, "statusOut") },
  };
  const msg = STATUS_MSG[status];
  if (!msg) return null;
  return (
    <div style={{
      position: "fixed", bottom: "calc(60px + env(safe-area-inset-bottom))", left: "50%", transform: "translateX(-50%)",
      width: "100%", maxWidth: 480, padding: "0 12px", zIndex: 198, boxSizing: "border-box",
    }}>
      <div
        onClick={onTap}
        style={{
          height: 44, borderRadius: T.radiusPill,
          background: "rgba(26,10,0,0.85)",
          backdropFilter: "blur(12px) saturate(180%)",
          WebkitBackdropFilter: "blur(12px) saturate(180%)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 16px", boxShadow: T.shadowLg, cursor: "pointer",
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: T.white, fontFamily: T.fontBody }}>
          {status === "out_for_delivery" ? (
            <>
              <span style={{ display: "inline-flex", alignItems: "center", marginRight: 6 }}>
                  <span className="bear-wobble" style={{
                    display: "inline-flex", alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "50%", width: 24, height: 24,
                    fontSize: 14
                  }}>🚴</span>
                </span>
              {msg.text}
            </>
          ) : (
            `${msg.icon} ${msg.text}`
          )}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: T.orange }}>{cc(lang, "track")}</span>
      </div>
    </div>
  );
}

// ─── StorePulse ───────────────────────────────────────────────────────────────
function StorePulse({ stats, products, usualCount }) {
  const deals = products.filter((p) => p.discount > 0).length;
  const lowStock = products.filter((p) => p.stock > 0 && p.stock < 5).length;
  const orderCount = Number(stats?.orderCount || 0);
  const signals = [
    orderCount > 0 ? `${orderCount} orders served` : null,
    deals > 0 ? `${deals} deals today` : null,
    usualCount > 0 ? `${usualCount} usuals ready` : null,
    lowStock > 0 ? `${lowStock} low-stock picks` : null,
  ].filter(Boolean).slice(0, 3);
  if (!signals.length) return null;
  return (
    <div style={{ display: "flex", gap: 8, overflowX: "auto", padding: "8px 12px 0" }}>
      {signals.map((signal) => (
        <div
          key={signal}
          style={{
            flexShrink: 0, background: T.white, border: `1px solid ${T.grey100}`,
            borderRadius: T.radiusPill, padding: "7px 12px", boxShadow: T.shadowSm,
            fontSize: 12, color: T.black, fontWeight: 700, fontFamily: T.fontBody
          }}
        >
          {signal}
        </div>
      ))}
    </div>
  );
}

// ─── BearRideOverlay ──────────────────────────────────────────────────────────
function BearRideOverlay({ show }) {
  if (!show) return null;
  return (
    <div className="bear-overlay-fade" style={{
      position: "fixed", inset: 0, zIndex: 500,
      background: "rgba(255,240,235,0.85)",
      display: "flex", alignItems: "center", justifyContent: "flex-start",
      pointerEvents: "none", overflow: "hidden",
    }}>
      <div className="bear-ride" style={{ display: "inline-block", marginTop: 80 }}>
        <svg width="140" height="90" viewBox="0 0 140 90" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Wheels */}
          <circle cx="28" cy="68" r="18" stroke="#333" strokeWidth="3" fill="none"/>
          <circle cx="110" cy="68" r="18" stroke="#333" strokeWidth="3" fill="none"/>
          <circle cx="28" cy="68" r="3" fill="#333"/>
          <circle cx="110" cy="68" r="3" fill="#333"/>
          {/* Spokes */}
          <line x1="28" y1="50" x2="28" y2="86" stroke="#555" strokeWidth="1.5"/>
          <line x1="10" y1="68" x2="46" y2="68" stroke="#555" strokeWidth="1.5"/>
          <line x1="110" y1="50" x2="110" y2="86" stroke="#555" strokeWidth="1.5"/>
          <line x1="92" y1="68" x2="128" y2="68" stroke="#555" strokeWidth="1.5"/>
          {/* Frame */}
          <line x1="28" y1="68" x2="69" y2="40" stroke="#E8470A" strokeWidth="3"/>
          <line x1="110" y1="68" x2="69" y2="40" stroke="#E8470A" strokeWidth="3"/>
          <line x1="69" y1="40" x2="69" y2="68" stroke="#E8470A" strokeWidth="3"/>
          <line x1="28" y1="68" x2="69" y2="68" stroke="#E8470A" strokeWidth="3"/>
          {/* Handlebar */}
          <line x1="105" y1="40" x2="94" y2="28" stroke="#555" strokeWidth="3"/>
          <line x1="94" y1="28" x2="82" y2="30" stroke="#555" strokeWidth="2.5"/>
          {/* Seat post & seat */}
          <line x1="64" y1="40" x2="64" y2="30" stroke="#555" strokeWidth="2.5"/>
          <rect x="56" y="27" width="18" height="5" rx="2.5" fill="#333"/>
          {/* Bear body */}
          <ellipse cx="76" cy="26" rx="13" ry="15" fill="#D4A017"/>
          {/* Bear head */}
          <circle cx="76" cy="11" r="10" fill="#D4A017"/>
          {/* Ears */}
          <circle cx="68" cy="3" r="4" fill="#D4A017"/>
          <circle cx="84" cy="3" r="4" fill="#D4A017"/>
          <circle cx="68" cy="3" r="2" fill="#E8A878"/>
          <circle cx="84" cy="3" r="2" fill="#E8A878"/>
          {/* Snout */}
          <ellipse cx="76" cy="14" rx="5.5" ry="3.5" fill="#E8A878"/>
          {/* Eyes */}
          <circle cx="71" cy="9" r="1.5" fill="#1A1A1A"/>
          <circle cx="81" cy="9" r="1.5" fill="#1A1A1A"/>
          <circle cx="71.7" cy="8.4" r="0.6" fill="#fff"/>
          <circle cx="81.7" cy="8.4" r="0.6" fill="#fff"/>
          {/* Nose */}
          <ellipse cx="76" cy="13.5" rx="2" ry="1.5" fill="#1A1A1A"/>
          {/* Smile */}
          <path d="M72 16 Q76 18.5 80 16" stroke="#1A1A1A" strokeWidth="1" fill="none" strokeLinecap="round"/>
          {/* Arms */}
          <line x1="65" y1="26" x2="55" y2="36" stroke="#D4A017" strokeWidth="5" strokeLinecap="round"/>
          <line x1="87" y1="24" x2="96" y2="30" stroke="#D4A017" strokeWidth="5" strokeLinecap="round"/>
          {/* Legs */}
          <line x1="70" y1="40" x2="60" y2="56" stroke="#D4A017" strokeWidth="4.5" strokeLinecap="round"/>
          <line x1="82" y1="40" x2="88" y2="56" stroke="#D4A017" strokeWidth="4.5" strokeLinecap="round"/>
          {/* Delivery bag */}
          <rect x="40" y="46" width="20" height="15" rx="3" fill="#E8470A"/>
          <line x1="40" y1="52" x2="60" y2="52" stroke="#fff" strokeWidth="1.5"/>
          <text x="46" y="60" fontSize="7" fill="#fff" fontWeight="bold">📦</text>
        </svg>
      </div>
      <div style={{ position: "absolute", top: "40%", left: 0, right: 0, textAlign: "center" }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700, color: "#E8470A", margin: 0 }}>
          🚴 Your order is on the way!
        </p>
      </div>
    </div>
  );
}

// ─── RatingModal ───────────────────────────────────────────────────────────────
function RatingModal({ show, orderId, stars, comment, onStars, onComment, onSubmit, onSkip }) {
  if (!show) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(26,10,0,0.5)", zIndex: 600, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div className="slide-up" style={{ width: "100%", maxWidth: 480, background: "#fff", borderRadius: "24px 24px 0 0", padding: "0 20px 48px", boxSizing: "border-box" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ width: 36, height: 4, background: "#D4D3D0", borderRadius: 100, margin: "12px auto 20px" }} />
        <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: "#1A0A00", margin: "0 0 6px", textAlign: "center" }}>
          How was your order? 🎉
        </p>
        <p style={{ fontSize: 13, color: "#8B6B50", textAlign: "center", margin: "0 0 24px", fontFamily: "'DM Sans', sans-serif" }}>
          Your feedback helps us improve
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 20 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              className={stars >= n ? "star-pop" : ""}
              onClick={() => onStars(n)}
              style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 36, lineHeight: 1, padding: 0, color: n <= stars ? "#D4A017" : "#D4D3D0", transition: "color 0.15s ease" }}
            >
              {n <= stars ? "★" : "☆"}
            </button>
          ))}
        </div>
        <textarea
          placeholder="Any comments? (optional)"
          value={comment}
          onChange={(e) => onComment(e.target.value)}
          style={{ width: "100%", height: 80, padding: "10px 12px", fontSize: 14, border: "1.5px solid #D4D3D0", borderRadius: 12, outline: "none", resize: "none", fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box", color: "#1A0A00", marginBottom: 16 }}
        />
        <button
          type="button"
          onClick={onSubmit}
          disabled={stars === 0}
          style={{ width: "100%", background: stars === 0 ? "#D4D3D0" : "#1A0A00", color: "#fff", border: "none", borderRadius: 100, height: 52, fontSize: 15, fontWeight: 700, cursor: stars === 0 ? "not-allowed" : "pointer", fontFamily: "'DM Sans', sans-serif", marginBottom: 10 }}
        >
          Submit Rating
        </button>
        <button type="button" onClick={onSkip} style={{ width: "100%", background: "transparent", border: "none", color: "#8B6B50", fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
          Skip
        </button>
      </div>
    </div>
  );
}

// ─── OrderHistoryCard ──────────────────────────────────────────────────────────
function OrderHistoryCard({ order, onReorder }) {
  const STATUS_COLOR = { pending: "#E8470A", accepted: "#1A6B3C", preparing: "#1A6B3C", out_for_delivery: "#E8470A", delivered: "#8B6B50", cancelled: "#DC2626" };
  const STATUS_LABEL = { pending: "Placed", accepted: "Accepted", preparing: "Preparing", out_for_delivery: "On the Way", delivered: "Delivered", cancelled: "Cancelled" };
  const date    = order.createdAt?.toDate ? order.createdAt.toDate() : null;
  const dateStr = date ? date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";
  const items   = order.items || [];
  const itemsStr = items.slice(0, 2).map((i) => i.name).join(", ") + (items.length > 2 ? ` +${items.length - 2} more` : "");
  const col = STATUS_COLOR[order.status] || "#8B6B50";
  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #F3ECE0", padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: "#8B6B50", fontFamily: "'DM Sans', sans-serif" }}>{dateStr}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: col, background: `${col}18`, borderRadius: 100, padding: "2px 8px", fontFamily: "'DM Sans', sans-serif" }}>
          {STATUS_LABEL[order.status] || order.status}
        </span>
      </div>
      <p style={{ fontSize: 13, color: "#1A0A00", margin: "0 0 3px", fontFamily: "'DM Sans', sans-serif", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {itemsStr || "Order"}
      </p>
      <p style={{ fontSize: 13, fontWeight: 700, color: "#1A0A00", margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
        ₹{order.total ?? "—"}
      </p>
      <button
        type="button"
        onClick={() => onReorder(order)}
        style={{
          width: "100%", height: 36, border: "none", borderRadius: 100,
          background: "#1A0A00", color: "#fff", fontSize: 13,
          fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
          cursor: "pointer", marginTop: 10
        }}
      >
        Reorder
      </button>
    </div>
  );
}

// ─── CustomerMenuPage ─────────────────────────────────────────────────────────
export default function CustomerMenuPage() {
  const { storeId } = useParams();
  const STORE_PATH = `stores/${storeId}/products`;
  // Paint from the last-known catalogue immediately; the Firestore read below
  // revalidates and replaces it. The server remains authoritative for product
  // availability and pricing when an order is submitted.
  const [products,        setProducts]        = useState(() => readProductsCache(storeId) || []);
  const [isStorePaused,      setIsStorePaused]      = useState(false);
  const [storeName,          setStoreName]          = useState("");
  const [storeExists,        setStoreExists]        = useState(null); // null = unknown, true/false after first snapshot
  const [storePhone,         setStorePhone]         = useState("");
  const [storeStats,         setStoreStats]         = useState(null);
  const [freeDeliveryEnabled, setFreeDeliveryEnabled] = useState(true);
  const [freeDeliveryAmount, setFreeDeliveryAmount] = useState(199);
  const [deliveryChargeAmount, setDeliveryChargeAmount] = useState(10);
  // Skip the full-page skeleton when we already have a cached catalogue to show.
  const [loading,         setLoading]         = useState(() => !readProductsCache(storeId));
  const [error,           setError]           = useState(null);
  const [cart,            setCart]            = useState(() => {
    // Restore the cart across refresh/close; stale product ids are ignored by
    // cartItems, which filters against the live catalog.
    try {
      const saved = JSON.parse(localStorage.getItem(`kirana_cart_${storeId}`) || "null");
      if (saved && typeof saved === "object" && !Array.isArray(saved)) {
        const clean = {};
        Object.entries(saved).forEach(([id, qty]) => {
          if (typeof qty === "number" && qty > 0 && qty < 1000) clean[id] = qty;
        });
        return clean;
      }
    } catch {}
    return {};
  });
  const [activeCategory,  setActiveCategory]  = useState(null);
  const [searchText,      setSearchText]      = useState("");
  const [showModal,       setShowModal]       = useState(false);
  const [showSummaryModal,setShowSummaryModal]= useState(false);
  const [showSuccess,     setShowSuccess]     = useState(false);
  const [orderLoading,    setOrderLoading]    = useState(false);
  const [orderError,      setOrderError]      = useState(null);
  const [name,            setName]            = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem(CUSTOMER_KEY) || "null");
      return c?.name || "";
    } catch { return ""; }
  });
  const [address,         setAddress]         = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem(CUSTOMER_KEY) || "null");
      return c?.address || "";
    } catch { return ""; }
  });
  const [deliveryLocation, setDeliveryLocation] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem(CUSTOMER_KEY) || "null");
      return normalizeDeliveryLocation(c?.deliveryLocation, c?.address);
    } catch { return null; }
  });
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError,   setLocationError]   = useState("");
  const [mapsReady,        setMapsReady]        = useState(false);
  const [mapsError,        setMapsError]        = useState("");
  const [reminderOptIn, setReminderOptIn] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem(CUSTOMER_KEY) || "null");
      return c?.reminderOptIn === true;
    } catch { return false; }
  });
  const [activeTab,       setActiveTab]       = useState("home");
  const [custLang,        setCustLang]        = useState(() => {
    try { return localStorage.getItem(CUSTOMER_LANG_KEY) === "hi" ? "hi" : "en"; } catch { return "en"; }
  });
  const [buyAgainIds,     setBuyAgainIds]     = useState(() => {
    try { return JSON.parse(localStorage.getItem(BUY_AGAIN_KEY) || "[]"); } catch { return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("kirana_wishlist") || "[]")); } catch { return new Set(); }
  });
  const [placedOrderId,   setPlacedOrderId]   = useState(null);
  const [liveOrderStatus, setLiveOrderStatus] = useState(null);
  const [orderHistory,      setOrderHistory]      = useState(() => {
    try { return JSON.parse(localStorage.getItem(ORDER_HISTORY_KEY) || "[]"); } catch { return []; }
  });
  const [pastOrders,        setPastOrders]        = useState([]);
  const [showBearAnimation, setShowBearAnimation] = useState(false);
  const [addedToast, setAddedToast] = useState(null); // { name } | null
  const [detailProduct, setDetailProduct] = useState(null);
  const addedToastTimer = useRef(null);
  const [showRatingModal,   setShowRatingModal]   = useState(false);
  const [ratingStars,       setRatingStars]       = useState(0);
  const [ratingComment,     setRatingComment]     = useState("");
  const [ratedOrderId,      setRatedOrderId]      = useState(null); // eslint-disable-line no-unused-vars
  const [showAllOrders,     setShowAllOrders]     = useState(false);

  // ── Customer / login state ────────────────────────────────────────────────
  const [customer, setCustomer] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CUSTOMER_KEY) || "null"); }
    catch { return null; }
  });
  const [showLoginScreen, setShowLoginScreen] = useState(() => {
    try { return !JSON.parse(localStorage.getItem(CUSTOMER_KEY) || "null"); }
    catch { return true; }
  });
  const [loginPhone,   setLoginPhone]   = useState("");
  const [loginOtp,     setLoginOtp]     = useState("");
  const [loginStep,    setLoginStep]    = useState("phone");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError,   setLoginError]   = useState("");
  const [otpSent,      setOtpSent]      = useState(false); // eslint-disable-line no-unused-vars

  // ── Profile tab state ─────────────────────────────────────────────────────
  const [showOrdersPage,    setShowOrdersPage]    = useState(false);
  const [showEditProfile,   setShowEditProfile]   = useState(false);

  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOSInstall,      setIsIOSInstall]      = useState(false);
  const [shareCopied,       setShareCopied]       = useState(false);
  const installPromptRef = useRef(null);

  const sectionRefs     = useRef({});
  const pillBarRef      = useRef(null);
  const scrollPositions = useRef({ home: 0, search: 0, cart: 0, categories: 0 });
  const searchInputRef  = useRef(null);
  const addressInputRef = useRef(null);
  const mapCanvasRef    = useRef(null);
  const mapInstanceRef  = useRef(null);
  const markerRef       = useRef(null);
  const autocompleteRef = useRef(null);
  const addressLatestRef = useRef(address);
  const prevStatusRef   = useRef(null);
  const sheetTouchRef   = useRef({ startY: null, startScrollTop: 0 });
  const orderAttemptIdRef = useRef(null);
  const mapsEnabled     = !!GOOGLE_MAPS_API_KEY.trim();
  // Maps is configured AND actually working. If the script fails to load (quota,
  // referer restriction, blocked network, offline) we must not keep demanding a
  // map pin the customer has no way to set, which silently blocked every order.
  // In that case we fall back to the typed address, which is the same payload
  // shape used when Maps is not configured at all.
  const mapsUsable      = mapsEnabled && !mapsError;
  const storeCallHref   = getStoreCallHref(storeId, storePhone);

  useEffect(() => {
    addressLatestRef.current = address;
  }, [address]);

  const persistDeliveryLocation = (nextLocation, addressText = addressLatestRef.current) => {
    const normalized = normalizeDeliveryLocation(nextLocation, addressText);
    if (!normalized) return null;
    setDeliveryLocation(normalized);
    setLocationError("");
    setCustomer((prev) => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        address: addressText.trim() || prev.address || "",
        deliveryLocation: normalized,
      };
      try { localStorage.setItem(CUSTOMER_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });
    return normalized;
  };

  const moveMapPin = (nextLocation, zoom = 16) => {
    if (!window.google?.maps || !mapInstanceRef.current || !markerRef.current || !nextLocation) return;
    const position = { lat: nextLocation.lat, lng: nextLocation.lng };
    markerRef.current.setPosition(position);
    mapInstanceRef.current.setCenter(position);
    mapInstanceRef.current.setZoom(zoom);
  };

  const handleShareStore = async () => {
    const storeUrl = `https://kiranaos.in/store/${storeId}`;
    // Share targets (e.g. WhatsApp) append `url` after `text`, so the text must
    // not contain the URL or the link shows up twice.
    if (navigator.share) {
      try { await navigator.share({ title: storeName, text: "Order from this local kirana store online:", url: storeUrl }); } catch (_) {}
      return;
    }
    try {
      await navigator.clipboard.writeText(`Order from this local kirana store online: ${storeUrl}`);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch (_) {}
  };

  const switchTab = (tab) => {
    if (tab === activeTab) return;
    scrollPositions.current[activeTab] = window.scrollY;
    setActiveTab(tab);
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollPositions.current[tab] || 0, behavior: "instant" });
    });
    if (tab === "search") setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  // Inject global styles
  useEffect(() => {
    const id = "kirana-global-v2";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      s.textContent = GLOBAL_CSS;
      document.head.appendChild(s);
    }
  }, []);

  useEffect(() => {
    if (!showModal || !mapsEnabled) return;
    let cancelled = false;
    setMapsReady(false);
    setMapsError("");

    loadGoogleMapsApi(GOOGLE_MAPS_API_KEY.trim())
      .then((google) => {
        if (cancelled || !mapCanvasRef.current) return;
        setMapsReady(true);

        const initial = normalizeDeliveryLocation(deliveryLocation, addressLatestRef.current) || {
          ...DEFAULT_MAP_CENTER,
          address: addressLatestRef.current,
          addressLine: addressLatestRef.current,
          placeId: null,
          source: "map_default",
        };
        const position = { lat: initial.lat, lng: initial.lng };
        const needsMap = !mapInstanceRef.current || mapInstanceRef.current.getDiv?.() !== mapCanvasRef.current;

        if (needsMap) {
          mapInstanceRef.current = new google.maps.Map(mapCanvasRef.current, {
            center: position,
            zoom: deliveryLocation ? 16 : 13,
            disableDefaultUI: true,
            zoomControl: false,
            clickableIcons: false,
            gestureHandling: "greedy",
          });
          markerRef.current = new google.maps.Marker({
            map: mapInstanceRef.current,
            position,
            draggable: true,
          });
          markerRef.current.addListener("dragend", () => {
            const markerPosition = markerRef.current?.getPosition?.();
            if (!markerPosition) return;
            const lat = markerPosition.lat();
            const lng = markerPosition.lng();
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              if (status === "OK" && results?.[0]) {
                const result = results[0];
                const nextAddress = result.formatted_address || addressLatestRef.current;
                const nextPlaceId = result.place_id || null;
                setAddress(nextAddress);
                addressLatestRef.current = nextAddress;
                persistDeliveryLocation({
                  lat,
                  lng,
                  address: nextAddress,
                  addressLine: nextAddress,
                  placeId: nextPlaceId,
                  source: "pin_drag_reverse_geocode",
                }, nextAddress);
              } else {
                // Geocoder failed — keep existing address, update coords only
                setLocationError("Pin updated, address could not refresh.");
                persistDeliveryLocation({
                  lat,
                  lng,
                  address: addressLatestRef.current,
                  addressLine: addressLatestRef.current,
                  placeId: null,
                  source: "pin_drag_reverse_geocode",
                }, addressLatestRef.current);
              }
            });
          });
        } else {
          moveMapPin(initial, deliveryLocation ? 16 : 13);
        }

        if (addressInputRef.current && autocompleteRef.current?.input !== addressInputRef.current) {
          autocompleteRef.current = null;
        }

        if (addressInputRef.current && !autocompleteRef.current) {
          const options = {
            fields: ["formatted_address", "geometry", "name", "place_id"],
          };
          try {
            options.componentRestrictions = { country: "in" };
            autocompleteRef.current = new google.maps.places.Autocomplete(addressInputRef.current, options);
          } catch (_) {
            delete options.componentRestrictions;
            autocompleteRef.current = new google.maps.places.Autocomplete(addressInputRef.current, options);
          }
          autocompleteRef.current.input = addressInputRef.current;
          autocompleteRef.current.addListener("place_changed", () => {
            const place = autocompleteRef.current?.getPlace?.();
            const placeLocation = place?.geometry?.location;
            if (!placeLocation) {
              setLocationError("Please choose a suggested address with a map pin.");
              return;
            }
            const nextAddress = place.formatted_address || place.name || addressLatestRef.current;
            setAddress(nextAddress);
            addressLatestRef.current = nextAddress;
            const nextLocation = persistDeliveryLocation({
              lat: placeLocation.lat(),
              lng: placeLocation.lng(),
              address: nextAddress,
              addressLine: nextAddress,
              placeId: place.place_id || null,
              source: "places_autocomplete",
            }, nextAddress);
            moveMapPin(nextLocation, 17);
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setMapsReady(false);
        setMapsError(err?.message || "Google Maps could not load.");
      });

    return () => {
      cancelled = true;
    };
  }, [showModal, mapsEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapsEnabled || !mapsReady || !deliveryLocation) return;
    moveMapPin(deliveryLocation, 16);
  }, [deliveryLocation, mapsEnabled, mapsReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Firebase session restore ──────────────────────────────────────────────
  // When Firebase Auth has a persisted session (e.g. after tab close / browser
  // restart), restore the customer's identity from that session so they are NOT
  // shown the OTP screen again.  We read claims.phone from the ID token and
  // cross-check against the localStorage entry so we never silently "log in" a
  // different phone number.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return; // no Firebase session — login screen shown by state default
      try {
        const tokenResult = await user.getIdTokenResult();
        const claims = tokenResult.claims || {};
        if (claims.role !== "customer") return; // owner token, skip
        const claimPhone = claims.phone || "";
        const stored = (() => {
          try { return JSON.parse(localStorage.getItem(CUSTOMER_KEY) || "null"); }
          catch { return null; }
        })();
        if (stored && stored.phone === claimPhone) {
          setDeliveryLocation(normalizeDeliveryLocation(stored.deliveryLocation, stored.address));
          // Session matches stored profile — user is already logged in locally, nothing to do.
          // setShowLoginScreen(false) already handled by the useState initialiser.
          return;
        }
        // Firebase session exists but localStorage is empty / stale.
        // Restore from Firestore customer record if available.
        if (claimPhone) {
          try {
            const customerSnap = await getDoc(doc(db, "stores", storeId, "customers", claimPhone));
            const serverCustomer = customerSnap.exists() ? customerSnap.data() : {};
            const restoredDeliveryLocation = normalizeDeliveryLocation(serverCustomer.deliveryLocation, serverCustomer.address || "");
            const restored = {
              phone: claimPhone,
              name: serverCustomer.name || "",
              address: serverCustomer.address || "",
              reminderOptIn: serverCustomer.reminderOptIn || false,
              ...(restoredDeliveryLocation ? { deliveryLocation: restoredDeliveryLocation } : {}),
            };
            setCustomer(restored);
            setName(restored.name);
            setAddress(restored.address);
            setDeliveryLocation(restoredDeliveryLocation);
            setReminderOptIn(restored.reminderOptIn);
            try { localStorage.setItem(CUSTOMER_KEY, JSON.stringify(restored)); } catch {}
            setShowLoginScreen(false);
          } catch (fsErr) {
            console.warn("[KiranaOS] Could not restore customer from Firestore:", fsErr.message);
          }
        }
      } catch (err) {
        console.warn("[KiranaOS] Customer session restore failed:", err.message);
      }
    });
    return () => unsub();
  }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch products
  useEffect(() => {
    let cancelled = false;
    const cached = readProductsCache(storeId);
    setProducts(cached || []);
    setLoading(!cached);
    setError(null);

    getDocs(collection(db, STORE_PATH))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((p) => p.active !== false);
        setProducts(data);
        setLoading(false);
        writeProductsCache(storeId, data);
      })
      .catch((err) => {
        if (cancelled) return;
        // If we painted from cache the customer still has a usable storefront,
        // so don't replace it with a full-page error.
        if (!readProductsCache(storeId)) setError(err.message);
        setLoading(false);
      });
    // Keyed on the store: the router reuses this component when only the
    // :storeId param changes, so with an empty dep array the catalogue stayed
    // on the previous store, and would now be written to the new store's
    // cache key.
    return () => { cancelled = true; };
  }, [storeId, STORE_PATH]);

  // Real-time store pause listener
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "stores", storeId), (snap) => {
      setStoreExists(snap.exists());
      setIsStorePaused(snap.exists() ? !!snap.data().storePaused : false);
      if (snap.exists()) {
        setStoreName(snap.data()?.storeName || snap.data()?.name || "");
        setStorePhone(snap.data()?.phone || "");
        setStoreStats(snap.data()?.stats || null);
        const storeData = snap.data();
        setFreeDeliveryEnabled(storeData?.freeDeliveryEnabled ?? true);
        setFreeDeliveryAmount(normalizeMoneySetting(storeData?.freeDeliveryAmount, 199));
        setDeliveryChargeAmount(normalizeMoneySetting(storeData?.deliveryChargeAmount, 10));
      }
    });
    return () => unsub();
  }, [storeId]);

  useEffect(() => {
    const canonicalUrl = `https://kiranaos.in/store/${storeId}`;
    const title = storeName || "KiranaOS";
    const description = `Order from ${title} — delivered to your door`;
    const storeSnap = products.find((product) => product.image);
    const image = storeSnap?.image || DEFAULT_OG_IMAGE;

    document.title = title;
    setCanonicalUrl(canonicalUrl);
    setHeadTag("meta[name='description']", { attr: "name", value: "description", content: description });
    setHeadTag("meta[property='og:title']", { attr: "property", value: "og:title", content: title });
    setHeadTag("meta[property='og:description']", { attr: "property", value: "og:description", content: description });
    setHeadTag("meta[property='og:image']", { attr: "property", value: "og:image", content: image });
    setHeadTag("meta[property='og:url']", { attr: "property", value: "og:url", content: canonicalUrl });
    setHeadTag("meta[property='og:type']", { attr: "property", value: "og:type", content: "website" });
  }, [products, storeId, storeName]);

  // Active pill scrolls into view on user tap only (no scroll spy)
  // Scroll active pill into view
  useEffect(() => {
    if (!activeCategory || !pillBarRef.current) return;
    const btn = pillBarRef.current.querySelector(`[data-cat="${activeCategory}"]`);
    if (btn) btn.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeCategory]);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const categories = useMemo(() => {
    const present = new Set(products.map((p) => getDisplayCategory(p.category)));
    const ordered = CATEGORY_ORDER.filter((c) => present.has(c));
    products.forEach((p) => {
      const dc = getDisplayCategory(p.category);
      if (!CATEGORY_ORDER.includes(dc) && !ordered.includes(dc)) ordered.push(dc);
    });
    return ordered;
  }, [products]);

  const popularIds = useMemo(() => {
    if (!products.length) return new Set();
    const step = Math.max(1, Math.floor(products.length / 10));
    const ids = new Set();
    for (let i = 0; i < 10; i++) ids.add(products[(i * step + 5) % products.length].id);
    return ids;
  }, [products]);

  const bestSellers = useMemo(() =>
    products.filter((p) => BEST_SELLER_NAMES.some((n) => p.name.toLowerCase().includes(n.toLowerCase()))),
    [products]
  );

  const productsById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const buyAgainProducts = useMemo(() => {
    return buyAgainIds.map((id) => productsById[id]).filter(Boolean).slice(0, 6);
  }, [buyAgainIds, productsById]);

  // Same-category products for the detail sheet. No merchant input needed: it
  // is the catalogue the store already has, filtered to what is orderable.
  const detailRelated = useMemo(() => {
    if (!detailProduct) return [];
    const cat = getDisplayCategory(detailProduct.category);
    return products
      .filter((p) => p.id !== detailProduct.id && getDisplayCategory(p.category) === cat && p.stock !== 0)
      .slice(0, 10);
  }, [detailProduct, products]);

  // Suggestions for the otherwise-blank search screen. Reuses what we already
  // know: this shopper's recent items, then the store's popular lines.
  const searchSuggestions = useMemo(() => {
    const recent = buyAgainIds.map((id) => productsById[id]).filter(Boolean).slice(0, 6);
    const recentIds = new Set(recent.map((p) => p.id));
    const popular = products
      .filter((p) => popularIds.has(p.id) && !recentIds.has(p.id) && p.stock !== 0)
      .slice(0, 8);
    return { recent, popular };
  }, [buyAgainIds, productsById, products, popularIds]);

  const usualProducts = useMemo(() => {
    const counts = {};
    pastOrders.forEach((order) => {
      (order.items || []).forEach((item) => {
        const product = findMatchingProduct(item, productsById, products);
        if (!product || product.stock === 0) return;
        counts[product.id] = (counts[product.id] || 0) + (Number(item.quantity) || 1);
      });
    });
    const fromOrders = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => productsById[id])
      .filter(Boolean)
      .slice(0, 8);
    return fromOrders.length ? fromOrders : buyAgainProducts;
  }, [buyAgainProducts, pastOrders, products, productsById]);

  const savedProducts = useMemo(
    () => products.filter((p) => wishlist.has(p.id)).slice(0, 12),
    [products, wishlist]
  );

  const grouped = useMemo(() => {
    const map = {};
    products.forEach((p) => { const dc = getDisplayCategory(p.category); (map[dc] = map[dc] || []).push(p); });
    return categories.filter((c) => map[c]).map((c) => ({ category: c, items: map[c] }));
  }, [products, categories]);

  const searchResults = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) => `${p.name} ${p.category} ${p.unit}`.toLowerCase().includes(q));
  }, [products, searchText]);

  const cartItems      = useMemo(() => products.filter((p) => cart[p.id] > 0).map((p) => ({ ...p, quantity: cart[p.id] })), [cart, products]);
  const basketSuggestions = useMemo(() => getCompanionProducts(cartItems, products, cart), [cartItems, products, cart]);
  const totalItems     = cartItems.reduce((s, i) => s + i.quantity, 0);
  const totalPrice     = cartItems.reduce((s, i) => s + getLineTotal(i), 0);
  const freeDeliveryThreshold = normalizeMoneySetting(freeDeliveryAmount, 199);
  const baseDeliveryCharge = normalizeMoneySetting(deliveryChargeAmount, 10);
  const deliveryCharge = freeDeliveryEnabled && totalPrice >= freeDeliveryThreshold ? 0 : baseDeliveryCharge;
  const grandTotal     = totalPrice + deliveryCharge;
  // Discount already applied across the basket, plus delivery if it was waived.
  // Purely derived from the same helpers used for the totals, no new math.
  const itemSavings    = cartItems.reduce(
    (s, i) => s + (getOriginalUnitPrice(i) - getEffectiveUnitPrice(i)) * (Number(i.quantity) || 0), 0
  );
  const deliverySaved  = freeDeliveryEnabled && deliveryCharge === 0 ? baseDeliveryCharge : 0;
  const totalSavings   = roundMoney(itemSavings + deliverySaved);

  useEffect(() => {
    try { localStorage.setItem(`kirana_cart_${storeId}`, JSON.stringify(cart)); } catch {}
  }, [cart, storeId]);

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const updateQty = (id, delta) => {
    // Adding an item only nudged the cart badge, which is easy to miss on a
    // phone. Confirm the add explicitly. Set outside the setCart updater so the
    // updater stays free of side effects.
    if (delta > 0) {
      const added = productsById[id];
      if (added) showAddedToast(added.name);
    }
    setCart((prev) => {
      const prevQty = prev[id] || 0;
      const next    = Math.max(prevQty + delta, 0);
      const updated = { ...prev };
      if (next === 0) delete updated[id]; else updated[id] = next;
      if (delta > 0 && prevQty === 0) {
        setBuyAgainIds((prevIds) => {
          const newIds = [id, ...prevIds.filter((i) => i !== id)].slice(0, 20);
          try { localStorage.setItem(BUY_AGAIN_KEY, JSON.stringify(newIds)); } catch {}
          return newIds;
        });
      }
      return updated;
    });
  };

  const showAddedToast = (name) => {
    setAddedToast({ name });
    if (addedToastTimer.current) clearTimeout(addedToastTimer.current);
    addedToastTimer.current = setTimeout(() => setAddedToast(null), 1800);
  };

  // Don't leave a timer running against an unmounted component.
  useEffect(() => () => { if (addedToastTimer.current) clearTimeout(addedToastTimer.current); }, []);

  const toggleWishlist = (id) => {
    setWishlist((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem("kirana_wishlist", JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const handleReorder = (order) => {
    const items = order?.items || [];
    if (!items.length) return;
    const resolved = items
      .map((item) => ({ product: findMatchingProduct(item, productsById, products), qty: Math.max(1, Number(item.quantity) || 1) }))
      .filter(({ product }) => product && product.stock !== 0);
    if (!resolved.length) {
      alert("Those items are not available right now.");
      return;
    }
    setCart((prev) => {
      const next = { ...prev };
      resolved.forEach(({ product, qty }) => {
        next[product.id] = (next[product.id] || 0) + qty;
      });
      return next;
    });
    setShowOrdersPage(false);
    switchTab("cart");
  };

  const scrollToCategory = (cat) => {
    setActiveCategory(cat);
    setTimeout(() => {
      const sectionEl = document.getElementById(`section-${cat}`);
      if (!sectionEl) return;
      const headerOffset    = 170;
      const elementPosition = sectionEl.getBoundingClientRect().top;
      const offsetPosition  = window.pageYOffset + elementPosition - headerOffset;
      window.scrollTo({
        top:      offsetPosition,
        behavior: "instant",
      });
    }, 10);
  };

  const handleRatingSubmit = async () => {
    const targetId = ratedOrderId || placedOrderId;
    if (!targetId || ratingStars === 0) return;
    try {
      await setDoc(doc(db, "stores", storeId, "ratings", targetId), {
        orderId: targetId,
        stars: ratingStars,
        comment: ratingComment.trim() || null,
        createdAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("[KiranaOS] Rating save failed:", err);
    } finally {
      setShowRatingModal(false);
    }
  };

  const handleFormConfirm = () => {
    if (!name.trim()) { alert("Please enter your name."); return; }
    if (!address.trim()) { alert("Please enter your delivery address."); return; }
    if (mapsUsable && !normalizeDeliveryLocation(deliveryLocation, address)) {
      setLocationError("Please choose or set your delivery pin.");
      return;
    }
    setShowModal(false);
    setShowSummaryModal(true);
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Location is not available in this browser.");
      return;
    }
    setLocationLoading(true);
    setLocationError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results?.[0]) {
            const result = results[0];
            const nextAddress = result.formatted_address || addressLatestRef.current;
            const nextPlaceId = result.place_id || null;
            setAddress(nextAddress);
            addressLatestRef.current = nextAddress;
            const nextLocation = persistDeliveryLocation({
              lat,
              lng,
              address: nextAddress,
              addressLine: nextAddress,
              placeId: nextPlaceId,
              source: "browser_geolocation_reverse_geocode",
            }, nextAddress);
            moveMapPin(nextLocation, 17);
          } else {
            // Geocoder failed — pin is still placed, address field unchanged
            setLocationError("Pin updated, address could not refresh.");
            const nextLocation = persistDeliveryLocation({
              lat,
              lng,
              address: addressLatestRef.current,
              addressLine: addressLatestRef.current,
              placeId: null,
              source: "browser_geolocation_reverse_geocode",
            }, addressLatestRef.current);
            moveMapPin(nextLocation, 17);
          }
          setLocationLoading(false);
        });
      },
      (err) => {
        setLocationError(err?.message || "Could not get your location.");
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
    );
  };

  const handleSendOtp = async () => {
    const phone = "+91" + loginPhone.trim();
    if (!/^\+91[6-9]\d{9}$/.test(phone)) {
      setLoginError("Please enter a valid 10-digit Indian mobile number");
      return;
    }
    setLoginLoading(true);
    setLoginError("");
    try {
      const res  = await fetch(`${API_BASE}/api/otp/send`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to send OTP");
      setLoginStep("otp");
      setOtpSent(true);
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (loginOtp.length !== 4) {
      setLoginError("Please enter the 4-digit OTP");
      return;
    }
    setLoginLoading(true);
    setLoginError("");
    try {
      const phone = "+91" + loginPhone.trim();
      const res   = await fetch(`${API_BASE}/api/otp/verify`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ phone, otp: loginOtp, storeId }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Invalid OTP");
      if (data.customToken) {
        await signInWithCustomToken(auth, data.customToken);
      }
      const serverCustomer = data.customer || {};
      const nextReminderOptIn = serverCustomer.reminderOptIn !== undefined ? serverCustomer.reminderOptIn : reminderOptIn;
      const nextDeliveryLocation = normalizeDeliveryLocation(serverCustomer.deliveryLocation || deliveryLocation, serverCustomer.address || address);
      const newCustomer = {
        phone,
        name: serverCustomer.name || name || "",
        address: serverCustomer.address || address || "",
        reminderOptIn: nextReminderOptIn,
        ...(nextDeliveryLocation ? { deliveryLocation: nextDeliveryLocation } : {}),
      };
      setName(newCustomer.name);
      setAddress(newCustomer.address);
      setDeliveryLocation(nextDeliveryLocation);
      setReminderOptIn(nextReminderOptIn);
      setCustomer(newCustomer);
      try { localStorage.setItem(CUSTOMER_KEY, JSON.stringify(newCustomer)); } catch {}
      setShowLoginScreen(false);
      setTimeout(() => {
        const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isRunningStandalone()) return;
        if (!isIOS && installPromptRef.current) {
          setIsIOSInstall(false);
          setShowInstallPrompt(true);
        } else if (isIOS && isSafari) {
          setIsIOSInstall(true);
          setShowInstallPrompt(true);
        }
      }, 1500);
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const saveCustomerProfile = async (updates) => {
    if (!customer?.phone) return;
    try {
      // Use fetchWithAuthRetry so a stale token is refreshed automatically
      // instead of failing silently and losing the customer's profile changes.
      await fetchWithAuthRetry(
        `${API_BASE}/api/customers/${storeId}/${encodeURIComponent(customer.phone)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates),
        },
      );
    } catch (err) {
      console.error("[KiranaOS] Customer profile save failed:", err);
    }
  };

  const closeCheckoutSheet = () => {
    setShowModal(false);
  };

  const closeSummarySheet = () => {
    setShowSummaryModal(false);
    setOrderError(null);
  };

  const goBackToCheckout = () => {
    setShowSummaryModal(false);
    setShowModal(true);
  };

  const getSheetDragHandlers = (onPullDown) => ({
    onTouchStart: (event) => {
      if (event.target?.closest?.("[data-no-sheet-drag='true']")) return;
      const touch = event.touches?.[0];
      if (!touch) return;
      sheetTouchRef.current = {
        startY: touch.clientY,
        startScrollTop: event.currentTarget.scrollTop,
      };
    },
    onTouchEnd: (event) => {
      if (event.target?.closest?.("[data-no-sheet-drag='true']")) {
        sheetTouchRef.current = { startY: null, startScrollTop: 0 };
        return;
      }
      const start = sheetTouchRef.current;
      const touch = event.changedTouches?.[0];
      sheetTouchRef.current = { startY: null, startScrollTop: 0 };
      if (!touch || start.startY === null) return;
      if (start.startScrollTop <= 2 && touch.clientY - start.startY > 80) onPullDown();
    },
  });

  const handleSendOrder = async () => {
    if (!address.trim()) {
      setOrderError("Please enter your delivery address.");
      return;
    }
    if (mapsUsable && !normalizeDeliveryLocation(deliveryLocation, address)) {
      setOrderError("Please choose or set your delivery pin.");
      return;
    }
    setOrderLoading(true);
    if (!orderAttemptIdRef.current) {
      orderAttemptIdRef.current = (window.crypto?.randomUUID) ? crypto.randomUUID() : `ord-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    setOrderError(null);
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), 10000);
    try {
      const payloadDeliveryLocation = mapsUsable ? getDeliveryLocationForPayload(deliveryLocation, address) : null;
      const payload = {
        storeId:       storeId,
        clientOrderId: orderAttemptIdRef.current,
        customerName:  name.trim(),
        customerPhone: customer?.phone || null,
        address:       address.trim() || null,
        items:         cartItems.map((i) => ({
          id:            i.id,
          name:          i.name,
          unit:          i.unit,
          quantity:      i.quantity,
          price:         roundMoney(getEffectiveUnitPrice(i)),
          originalPrice: roundMoney(getOriginalUnitPrice(i)),
          discount:      normalizeDiscountPercent(i.discount),
          lineTotal:     roundMoney(getLineTotal(i)),
        })),
        subtotal:      roundMoney(totalPrice),
        deliveryCharge: roundMoney(deliveryCharge),
        total:         roundMoney(grandTotal),
        reminderOptIn,
        ...(payloadDeliveryLocation ? { deliveryLocation: payloadDeliveryLocation } : {}),
      };
      const res = await fetch("https://kiranaos-api-1036279469035.asia-south1.run.app/api/orders", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
        signal:  controller.signal,
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setShowSummaryModal(false);
      setCart({});
      orderAttemptIdRef.current = null;
      const newOrderId = data.orderId || null;
      setPlacedOrderId(newOrderId);
      if (customer) {
        const safeDeliveryLocation = mapsUsable ? normalizeDeliveryLocation(deliveryLocation, address) : null;
        const updated = {
          ...customer,
          address: address.trim() || customer.address,
          name: name.trim() || customer.name,
          reminderOptIn,
          ...(safeDeliveryLocation ? { deliveryLocation: safeDeliveryLocation } : {}),
        };
        setCustomer(updated);
        try { localStorage.setItem(CUSTOMER_KEY, JSON.stringify(updated)); } catch {}
      }
      prevStatusRef.current = null;
      setLiveOrderStatus("pending");
      if (newOrderId) {
        try {
          const existing = JSON.parse(localStorage.getItem(ORDER_HISTORY_KEY) || "[]");
          existing.unshift(newOrderId);
          localStorage.setItem(ORDER_HISTORY_KEY, JSON.stringify(existing.slice(0, 20)));
          setOrderHistory(existing.slice(0, 20));
        } catch {}
      }
      setShowSuccess(true);
    } catch (err) {
      console.error("[KiranaOS] Order placement failed:", err.name, err.message, err);
      if (err.name === "AbortError") {
        setOrderError("Request timed out. Please check your connection and try again.");
      } else {
        setOrderError(`Something went wrong: ${err.message}`);
      }
    } finally {
      clearTimeout(timeoutId);
      setOrderLoading(false);
    }
  };

  // Live order status listener
  useEffect(() => {
    if (!placedOrderId) return;
    const unsub = onSnapshot(
      doc(db, "stores", storeId, "orders", placedOrderId),
      (snap) => {
        if (snap.exists()) {
          const newStatus = snap.data().status;
          const prev = prevStatusRef.current;
          prevStatusRef.current = newStatus;
          setLiveOrderStatus(newStatus);
          if (prev !== null && prev !== "out_for_delivery" && newStatus === "out_for_delivery") {
            setShowBearAnimation(true);
            setTimeout(() => setShowBearAnimation(false), 4200);
          }
          if (prev !== null && prev !== "delivered" && newStatus === "delivered") {
            setTimeout(() => { setRatingStars(0); setRatingComment(""); setShowRatingModal(true); }, 1000);
            setTimeout(() => { setPlacedOrderId(null); setLiveOrderStatus(null); }, 6000);
          }
        }
      },
      (err) => console.error("[KiranaOS] Order status listener error:", err)
    );
    return () => unsub();
  }, [placedOrderId]);

  // Fetch past order details from Firestore
  useEffect(() => {
    if (!orderHistory.length) { setPastOrders([]); return; }
    Promise.all(orderHistory.map((id) => getDoc(doc(db, "stores", storeId, "orders", id))))
      .then((snaps) => setPastOrders(snaps.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() }))))
      .catch((err) => console.error("[KiranaOS] Order history fetch failed:", err));
  }, [orderHistory]);

  // PWA install prompt
  useEffect(() => {
    const canShowPrompt = () => {
      if (isRunningStandalone()) return false;
      try {
        const seenAt = Number(localStorage.getItem(INSTALL_PROMPT_SEEN_KEY) || 0);
        return !seenAt || Date.now() - seenAt > 1000 * 60 * 60 * 24 * 7;
      } catch {
        return true;
      }
    };

    const markPromptSeen = () => {
      try { localStorage.setItem(INSTALL_PROMPT_SEEN_KEY, String(Date.now())); } catch {}
    };

    const maybeShowPrompt = (iosInstall = false) => {
      if (!canShowPrompt()) return;
      window.setTimeout(() => {
        if (!canShowPrompt()) return;
        setIsIOSInstall(iosInstall);
        setShowInstallPrompt(true);
        markPromptSeen();
      }, 1200);
    };

    const handler = (e) => {
      e.preventDefault();
      installPromptRef.current = e;
      maybeShowPrompt(false);
    };

    window.addEventListener('beforeinstallprompt', handler);

    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIOS && isSafari) {
      maybeShowPrompt(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const contentPb = totalItems > 0
    ? "calc(130px + env(safe-area-inset-bottom))"
    : "calc(72px + env(safe-area-inset-bottom))";

  // ── Skeleton / error ──────────────────────────────────────────────────────────
  if (loading) return (
    <div style={S.page}>
      <div style={S.shell}>
        <div style={{ background: T.white, height: 64, borderBottom: `1px solid ${T.grey100}` }} />
        <div style={{ padding: "6px 12px 8px" }}>
          <div style={{ height: 48, borderRadius: T.radiusPill, background: T.grey100 }} className="shimmer" />
        </div>
        <div style={{ ...S.skeletonGrid, marginTop: 12 }}>
          {[0,1,2,3,4,5].map((i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div style={S.page}>
      <div style={{ ...S.shell, ...S.centered }}>
        <p>Couldn't load products.</p>
        <p style={{ fontSize: 12 }}>{error}</p>
        <button style={S.shopNowBtn} onClick={() => window.location.reload()}>Try again</button>
      </div>
    </div>
  );

  // ── Renderers ─────────────────────────────────────────────────────────────────
  const renderHomeTab = () => {
    if (isStorePaused) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", padding: "40px 24px", textAlign: "center" }}>
          <p style={{ fontSize: 16, fontWeight: 600, color: T.black, fontFamily: T.fontDisplay, margin: "0 0 16px" }}>{storeName}</p>
          <p style={{ fontSize: 28, fontWeight: 700, color: T.black, fontFamily: T.fontDisplay, margin: "0 0 10px" }}>दुकान अभी बंद है</p>
          <p style={{ fontSize: 14, color: T.grey500, fontFamily: T.fontBody, margin: 0 }}>This store is temporarily closed. Please check back soon.</p>
        </div>
      );
    }
    return (
    <div style={{ paddingBottom: contentPb }} className="fade-up">
      {/* Hero banner */}
      {freeDeliveryEnabled && (
        <div style={S.heroBanner}>
          <p style={S.heroBannerText}>
            {freeDeliveryThreshold <= 0
              ? cc(custLang, "freeDeliveryEvery")
              : totalPrice > 0 && totalPrice < freeDeliveryThreshold
                ? (custLang === "hi" ? `फ्री डिलीवरी के लिए ₹${formatMoneyAmount(freeDeliveryThreshold - totalPrice)} और जोड़ें` : `Add ₹${formatMoneyAmount(freeDeliveryThreshold - totalPrice)} more for free delivery`)
                : (custLang === "hi" ? `₹${formatMoneyAmount(freeDeliveryThreshold)} से ऊपर फ्री डिलीवरी` : `Free delivery above ₹${formatMoneyAmount(freeDeliveryThreshold)}`)}
          </p>
        </div>
      )}

      <StorePulse stats={storeStats} products={products} usualCount={usualProducts.length} />

      {basketSuggestions.length > 0 && (
        <div>
          <p style={S.featuredTitle}>{cc(custLang, "pairsWell")}</p>
          <div style={S.featuredScroll}>
            {basketSuggestions.slice(0, 6).map((p) => <FeaturedCard key={p.id} product={p} onTap={() => updateQty(p.id, 1)} />)}
            <div style={{ minWidth: 16, flexShrink: 0 }} />
          </div>
        </div>
      )}

      {/* Category grid — 2-col */}
      {categories.length > 0 && (
        <div>
          <p style={S.catGridTitle}>{cc(custLang, "shopByCategory")}</p>
          <div style={S.catGrid}>
            {categories.map((cat) => {
              const catItems = grouped.find((g) => g.category === cat)?.items || [];
              return <CategoryGridCard key={cat} cat={cat} items={catItems} onTap={() => scrollToCategory(cat)} />;
            })}
          </div>
        </div>
      )}

      {/* Your Usuals */}
      {usualProducts.length > 0 && (
        <div>
          <p style={S.featuredTitle}>{cc(custLang, "yourUsuals")}</p>
          <div style={S.featuredScroll}>
            {usualProducts.map((p) => <FeaturedCard key={p.id} product={p} onTap={() => updateQty(p.id, 1)} />)}
            <div style={{ minWidth: 16, flexShrink: 0 }} />
          </div>
        </div>
      )}

      {/* Saved items (wishlist) */}
      {savedProducts.length > 0 && (
        <div>
          <p style={S.featuredTitle}>{cc(custLang, "savedItems")}</p>
          <div style={S.featuredScroll}>
            {savedProducts.map((p) => <FeaturedCard key={p.id} product={p} onTap={() => updateQty(p.id, 1)} />)}
            <div style={{ minWidth: 16, flexShrink: 0 }} />
          </div>
        </div>
      )}

      {/* Best Sellers */}
      {bestSellers.length > 0 && (
        <div>
          <p style={S.featuredTitle}>{cc(custLang, "bestSellers")}</p>
          <div style={S.featuredScroll}>
            {bestSellers.map((p) => <FeaturedCard key={p.id} product={p} onTap={() => updateQty(p.id, 1)} />)}
            <div style={{ minWidth: 16, flexShrink: 0 }} />
          </div>
        </div>
      )}

      {/* Category product sections */}
      {grouped.map(({ category, items }, index) => (
        <div key={category} id={`section-${category}`} ref={(el) => (sectionRefs.current[category] = el)}>
          <p style={S.sectionHeader}>{category}</p>
          <LazySection eager={index < 2}>
            <div style={S.grid}>
              {items.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  quantity={cart[p.id] || 0}
                  onUpdate={updateQty}
                  isPopular={popularIds.has(p.id)}
                  wishlisted={wishlist.has(p.id)}
                  onWishlist={toggleWishlist}
                  onOpen={() => setDetailProduct(p)}
                />
              ))}
            </div>
          </LazySection>
        </div>
      ))}

      {/* Share this store */}
      <div style={{ margin: "32px 16px 16px", padding: "18px 20px", background: T.grey100, borderRadius: T.radiusMd, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <p style={{ fontFamily: T.fontBody, fontSize: 13, fontWeight: 600, color: T.black, margin: "0 0 2px" }}>Like this store?</p>
          <p style={{ fontFamily: T.fontBody, fontSize: 12, color: T.grey500, margin: 0 }}>Share it with friends and family.</p>
        </div>
        <button
          type="button"
          onClick={handleShareStore}
          style={{ background: T.orange, color: T.white, border: "none", borderRadius: T.radiusSm, padding: "9px 18px", fontSize: 13, fontWeight: 500, fontFamily: T.fontBody, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {shareCopied ? "Link copied!" : "Share store"}
        </button>
      </div>

      {/* Powered by badge */}
      <BrandFooter />

    </div>
    );
  };

  const renderSearchTab = () => (
    <div style={{ ...S.searchTabWrap, paddingBottom: contentPb }}>
      {searchText.trim() ? (
        <>
          <p style={{ fontSize: 13, color: T.grey500, margin: "0 0 10px", fontFamily: T.fontBody }}>
            {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for "{searchText}"
          </p>
          {searchResults.length === 0 ? (
            <div style={{ textAlign: "center", marginTop: 60 }}>
              <p style={{ color: T.grey300, fontSize: 14, fontFamily: T.fontBody, margin: "0 0 14px" }}>
                {custLang === "hi" ? `"${searchText}" के लिए कुछ नहीं मिला` : `No products found for "${searchText}"`}
              </p>
              {storeCallHref && (
                <button
                  type="button"
                  style={{ background: T.orangeLight, color: T.orange, border: "none", borderRadius: T.radiusPill, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody }}
                  onClick={() => { window.location.href = storeCallHref; }}
                >
                  {cc(custLang, "callStore")}
                </button>
              )}
            </div>
          ) : (
            searchResults.map((p) => (
              <SearchResultRow key={p.id} product={p} quantity={cart[p.id] || 0} onUpdate={updateQty} onOpen={() => setDetailProduct(p)} />
            ))
          )}
        </>
      ) : (
        // An empty search screen used to be a single line of grey text. Give it
        // something tappable: what this shopper bought before, then the store's
        // popular lines.
        <div style={{ padding: "18px 14px 0" }}>
          {searchSuggestions.recent.length > 0 && (
            <>
              <p style={S.searchSuggestHeading}>Buy again</p>
              <div style={S.searchSuggestWrap}>
                {searchSuggestions.recent.map((p) => (
                  <button key={p.id} style={S.searchSuggestChip} type="button" onClick={() => setSearchText(p.name)}>
                    {p.name}
                  </button>
                ))}
              </div>
            </>
          )}
          {searchSuggestions.popular.length > 0 && (
            <>
              <p style={S.searchSuggestHeading}>Popular right now</p>
              <div style={S.searchSuggestWrap}>
                {searchSuggestions.popular.map((p) => (
                  <button key={p.id} style={S.searchSuggestChip} type="button" onClick={() => setSearchText(p.name)}>
                    {p.name}
                  </button>
                ))}
              </div>
            </>
          )}
          {!searchSuggestions.recent.length && !searchSuggestions.popular.length && (
            <p style={{ textAlign: "center", color: T.grey300, marginTop: 60, fontSize: 14, fontFamily: T.fontBody }}>
              {cc(custLang, "searchStart")}
            </p>
          )}
        </div>
      )}
    </div>
  );

  const renderCartTab = () => {
    if (cartItems.length === 0) {
      return (
        <div style={{ ...S.emptyCartWrap, paddingBottom: "72px" }}>
          <span style={{ fontSize: 56 }}>🛍️</span>
          <p style={S.emptyCartTitle}>{cc(custLang, "cartEmptyTitle")}</p>
          <p style={S.emptyCartSub}>{cc(custLang, "cartEmptySub")}</p>
          <button style={S.shopNowBtn} type="button" onClick={() => switchTab("home")}>{cc(custLang, "browseProducts")}</button>
        </div>
      );
    }
    return (
      <div style={{ ...S.cartTabWrap, paddingBottom: contentPb }}>
        {freeDeliveryEnabled && freeDeliveryThreshold > 0 && (
          <div style={{ background: totalPrice >= freeDeliveryThreshold ? "#E8F5EE" : T.orangeLight, borderRadius: T.radiusMd, padding: "10px 14px", marginBottom: 10 }}>
            <p style={{ fontSize: 12.5, fontWeight: 600, color: totalPrice >= freeDeliveryThreshold ? T.green : T.orange, fontFamily: T.fontBody, margin: 0 }}>
              {totalPrice >= freeDeliveryThreshold
                ? cc(custLang, "freeDeliveryUnlocked")
                : (custLang === "hi" ? `फ्री डिलीवरी के लिए ₹${formatMoneyAmount(freeDeliveryThreshold - totalPrice)} और जोड़ें` : `Add ₹${formatMoneyAmount(freeDeliveryThreshold - totalPrice)} more for free delivery`)}
            </p>
          </div>
        )}
        {cartItems.map((item) => <CartRow key={item.id} item={item} onUpdate={updateQty} />)}
        {basketSuggestions.length > 0 && (
          <div style={{ margin: "14px -12px 2px" }}>
            <p style={{ ...S.featuredTitle, marginTop: 0 }}>{cc(custLang, "completeBasket")}</p>
            <div style={S.featuredScroll}>
              {basketSuggestions.map((p) => <FeaturedCard key={p.id} product={p} onTap={() => updateQty(p.id, 1)} />)}
              <div style={{ minWidth: 16, flexShrink: 0 }} />
            </div>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0 0", borderTop: `1px solid ${T.grey100}` }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: T.black, fontFamily: T.fontBody }}>{cc(custLang, "total")}</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: T.black, fontFamily: T.fontBody }}>₹{totalPrice.toFixed(2).replace(/\.00$/, "")}</span>
        </div>
      </div>
    );
  };

  const renderCategoriesTab = () => (
    <div style={{ ...S.catTabWrap, paddingBottom: contentPb }}>
      {grouped.map(({ category, items }) => {
        const imgSrc = pickCategoryCardImage(category, items);
        return (
          <div
            key={category}
            style={S.catTabRow}
            onClick={() => { scrollToCategory(category); switchTab("home"); }}
            role="button"
            tabIndex={0}
          >
            {imgSrc
              ? <img style={S.catTabRowImg} src={imgSrc} alt={category} loading="lazy" />
              : <div style={{ ...S.catTabRowImg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: T.grey300 }}>📦</div>
            }
            <div style={S.catTabRowInfo}>
              <p style={S.catTabRowName}>{category}</p>
              <p style={S.catTabRowCount}>{items.length} products</p>
            </div>
            <span style={S.catTabRowChevron}>›</span>
          </div>
        );
      })}
    </div>
  );

  const renderProfileTab = () => {
    const totalSpent  = pastOrders.reduce((s, o) => s + (o.total || 0), 0);
    const firstYear   = pastOrders.length > 0
      ? (pastOrders[pastOrders.length - 1]?.createdAt?.toDate?.()?.getFullYear?.() || new Date().getFullYear())
      : null;
    const initial = customer?.name ? customer.name.charAt(0).toUpperCase() : "?";
    return (
      <div style={{ paddingBottom: contentPb }}>
        {/* Avatar + identity */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 16px 20px", borderBottom: `1px solid ${T.grey100}` }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: T.orange, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 32, fontWeight: 700, color: T.white, fontFamily: T.fontBody }}>{initial}</span>
          </div>
          <p style={{ fontFamily: T.fontDisplay, fontSize: 20, fontWeight: 700, color: T.black, margin: "0 0 3px" }}>
            {customer?.name || "Guest"}
          </p>
          <p style={{ fontSize: 13, color: T.grey500, margin: "0 0 2px", fontFamily: T.fontBody }}>{customer?.phone}</p>
          <p style={{ fontSize: 12, color: T.grey500, margin: "0 0 10px", fontFamily: T.fontBody }}>{customer?.address || "No address saved"}</p>
          <button
            type="button"
            onClick={() => setShowEditProfile(true)}
            style={{ background: "transparent", border: "none", color: T.orange, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.fontBody }}
          >
            Edit Profile
          </button>
        </div>

        {/* Stats row */}
        {pastOrders.length > 0 && (
          <div style={{ display: "flex", gap: 10, padding: "16px 16px 0" }}>
            {[
              { label: "Orders",  value: pastOrders.length },
              { label: "Spent",   value: `₹${totalSpent}` },
              { label: "Since",   value: firstYear || "New" },
            ].map(({ label, value }) => (
              <div key={label} style={{ flex: 1, background: T.grey100, borderRadius: T.radiusMd, padding: 12, textAlign: "center" }}>
                <p style={{ fontSize: 11, color: T.grey500, margin: "0 0 4px", fontFamily: T.fontBody }}>{label}</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: T.black, margin: 0, fontFamily: T.fontBody }}>{value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Menu list */}
        <div style={{ padding: "16px 0" }}>
          <div style={S.catTabRow} onClick={() => setShowOrdersPage(true)}>
            <span style={{ fontSize: 24 }}>📦</span>
            <div style={S.catTabRowInfo}>
              <p style={S.catTabRowName}>My Orders</p>
              {pastOrders.length > 0 && (
                <p style={{ fontSize: 11, color: T.grey500, margin: 0,
                  fontFamily: T.fontBody }}>
                  {pastOrders.length} order{pastOrders.length !== 1 ? "s" : ""}
                </p>
              )}
            </div>
            <span style={S.catTabRowChevron}>›</span>
          </div>

          {placedOrderId && liveOrderStatus !== "delivered" && (
            <div style={S.catTabRow} onClick={() => setShowSuccess(true)}>
              <span style={{ fontSize: 24 }}>🛍️</span>
              <div style={S.catTabRowInfo}><p style={S.catTabRowName}>Active Order</p></div>
              <span style={S.catTabRowChevron}>›</span>
            </div>
          )}

          <div style={S.catTabRow} onClick={() => {
            const next = !reminderOptIn;
            setReminderOptIn(next);
            const updated = { ...customer, reminderOptIn: next };
            setCustomer(updated);
            try { localStorage.setItem(CUSTOMER_KEY, JSON.stringify(updated)); } catch {}
            saveCustomerProfile({ reminderOptIn: next });
          }}>
            <span style={{ fontSize: 24 }}>{reminderOptIn ? "🔔" : "🔕"}</span>
            <div style={S.catTabRowInfo}>
              <p style={S.catTabRowName}>WhatsApp Reminders</p>
              <p style={{ fontSize: 11, color: T.grey500, margin: 0, fontFamily: T.fontBody }}>
                {reminderOptIn ? "Helpful reorder reminders are on" : "Reminders are off"}
              </p>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: reminderOptIn ? T.green : T.grey500, fontFamily: T.fontBody }}>
              {reminderOptIn ? "ON" : "OFF"}
            </span>
          </div>

          {storeCallHref && (
            <div
              style={{ ...S.catTabRow, cursor: "pointer" }}
              onClick={() => { window.location.href = storeCallHref; }}
            >
              <span style={{ fontSize: 24 }}>📞</span>
              <div style={S.catTabRowInfo}><p style={S.catTabRowName}>Call Store</p></div>
              <span style={S.catTabRowChevron}>›</span>
            </div>
          )}

          <div style={S.catTabRow} onClick={() => {
            if (window.confirm("Log out?")) {
              localStorage.removeItem(CUSTOMER_KEY);
              localStorage.removeItem(ORDER_HISTORY_KEY);
              setCustomer(null);
              setShowLoginScreen(true);
              setCart({});
              setPlacedOrderId(null);
              setLiveOrderStatus(null);
            }
          }}>
            <span style={{ fontSize: 24 }}>🚪</span>
            <div style={S.catTabRowInfo}><p style={{ ...S.catTabRowName, color: T.red }}>Logout</p></div>
            <span style={S.catTabRowChevron}>›</span>
          </div>
        </div>

        <BrandFooter />

        {/* Edit profile bottom sheet */}
        {showEditProfile && (
          <div style={S.overlay} onClick={() => setShowEditProfile(false)}>
            <div style={S.sheet} className="slide-up" onClick={(e) => e.stopPropagation()}>
              <button style={S.sheetHandleBtn} type="button" aria-label="Close profile editor" onClick={() => setShowEditProfile(false)}>
                <span style={S.sheetHandle} />
              </button>
              <p style={S.sheetTitle}>Edit Profile</p>
              <div style={S.formGroup}>
                <label style={S.formLabel}>Name</label>
                <input
                  style={S.formInput}
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div style={S.formGroup}>
                <label style={S.formLabel}>Delivery Address</label>
                <input
                  style={S.formInput}
                  type="text"
                  placeholder="Flat no., landmark…"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                  }}
                />
              </div>
              <button
                style={S.placeOrderBtn}
                type="button"
                onClick={() => {
                  const updated = { ...customer, name: name.trim(), address: address.trim(), reminderOptIn };
                  setCustomer(updated);
                  try { localStorage.setItem(CUSTOMER_KEY, JSON.stringify(updated)); } catch {}
                  saveCustomerProfile({ name: updated.name, address: updated.address, reminderOptIn });
                  setShowEditProfile(false);
                }}
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderOrdersPage = () => (
    <div style={{ paddingBottom: contentPb }} className="fade-up">
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "16px 16px 12px",
        borderBottom: `1px solid ${T.grey100}`
      }}>
        <button
          type="button"
          onClick={() => setShowOrdersPage(false)}
          style={{ background: "transparent", border: "none", cursor: "pointer",
            padding: 0, fontSize: 22, color: T.black, lineHeight: 1,
            display: "flex", alignItems: "center" }}
        >
          ←
        </button>
        <p style={{ fontFamily: T.fontDisplay, fontSize: 20, fontWeight: 700,
          color: T.black, margin: 0 }}>My Orders</p>
      </div>

      {pastOrders.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", padding: "80px 24px", gap: 12 }}>
          <span style={{ fontSize: 48 }}>📦</span>
          <p style={{ fontFamily: T.fontDisplay, fontSize: 18, fontWeight: 700,
            color: T.black, margin: 0 }}>No orders yet</p>
          <p style={{ fontFamily: T.fontBody, fontSize: 13, color: T.grey500,
            margin: 0, textAlign: "center" }}>
            Your order history will appear here
          </p>
          <button
            type="button"
            onClick={() => { setShowOrdersPage(false); switchTab("home"); }}
            style={{ ...S.placeOrderBtn, marginTop: 8, width: "auto",
              padding: "0 32px", height: 44, fontSize: 14 }}
          >
            Start Shopping
          </button>
        </div>
      ) : (
        <div style={{ padding: "12px 16px" }}>
          <p style={{ fontFamily: T.fontBody, fontSize: 12, color: T.grey500,
            margin: "0 0 12px" }}>
            {pastOrders.length} order{pastOrders.length !== 1 ? "s" : ""}
          </p>
          {pastOrders.map((order) => (
            <OrderHistoryCard key={order.id} order={order} onReorder={handleReorder} />
          ))}
        </div>
      )}
    </div>
  );

  const TABS = [
    { id: "home",       Icon: IconHome,    label: cc(custLang, "navHome") },
    { id: "profile",    Icon: IconProfile, label: cc(custLang, "navProfile") },
    { id: "search",     Icon: IconSearch,  label: cc(custLang, "navSearch") },
    { id: "categories", Icon: IconGrid,    label: cc(custLang, "navCategories") },
  ];

  // Store does not exist (never created, or deleted). Never fall back to the
  // "Demo Kirana" default. Show a clear unavailable state instead.
  if (storeExists === false) {
    return (
      <div style={S.page}>
        <div style={S.shell}>
          <div style={{ minHeight: "70vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 24 }}>
            <h1 style={{ fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 700, color: T.black, margin: "0 0 8px" }}>
              Store not available
            </h1>
            <p style={{ fontFamily: T.fontBody, fontSize: 15, color: T.grey500, lineHeight: 1.6, margin: "0 0 24px", maxWidth: 320 }}>
              This store is no longer available or the link may be incorrect. Please check the link with the shopkeeper.
            </p>
            <a href="https://kiranaos.in/how-it-works" style={{ fontFamily: T.fontBody, fontSize: 14, fontWeight: 600, color: T.green, textDecoration: "none" }}>
              Powered by KiranaOS
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (showLoginScreen) {
    return (
      <div style={S.page}>
        <div style={S.shell}>
          <LoginScreen
            storeName={storeName}
            loginPhone={loginPhone}
            setLoginPhone={setLoginPhone}
            loginOtp={loginOtp}
            setLoginOtp={setLoginOtp}
            loginStep={loginStep}
            loginLoading={loginLoading}
            loginError={loginError}
            onSendOtp={handleSendOtp}
            onVerifyOtp={handleVerifyOtp}
            onBack={() => { setLoginStep("phone"); setLoginError(""); setLoginOtp(""); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.shell}>

        {/* ── Sticky header ─────────────────────────────────────────────────── */}
        <div style={S.stickyHeader}>
          <div style={S.topBar}>
            <div style={S.storeInfo}>
              <span style={S.storeName}>{storeName}</span>
              <span style={S.storeTagline}>{cc(custLang, "tagline")}</span>
            </div>
            <div style={S.topRight}>
              <button
                type="button"
                aria-label={custLang === "hi" ? "Switch to English" : "हिन्दी में देखें"}
                style={{ background: T.grey100, border: "none", borderRadius: T.radiusPill, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: T.black, cursor: "pointer", fontFamily: T.fontBody, marginRight: 8 }}
                onClick={() => setCustLang((l) => {
                  const next = l === "hi" ? "en" : "hi";
                  try { localStorage.setItem(CUSTOMER_LANG_KEY, next); } catch {}
                  return next;
                })}
              >
                {custLang === "hi" ? "EN" : "हिं"}
              </button>
              <button style={S.cartBtn} type="button" onClick={() => switchTab("cart")} aria-label="Cart">
                <IconBag color={T.black} />
                {totalItems > 0 && <span style={S.cartBadge}>{totalItems}</span>}
              </button>
            </div>
          </div>

          {/* Search bar */}
          <div style={S.searchWrap}>
            <div
              style={{ ...S.searchBar, cursor: activeTab !== "search" ? "pointer" : "text" }}
              onClick={() => { if (activeTab !== "search") switchTab("search"); }}
            >
              <span style={S.searchIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.grey500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              {activeTab === "search" ? (
                <input
                  ref={searchInputRef}
                  style={S.searchInput}
                  type="text"
                  placeholder={cc(custLang, "searchPlaceholder")}
                  value={searchText}
                  autoFocus
                  onChange={(e) => setSearchText(e.target.value)}
                />
              ) : (
                <span style={{ ...S.searchInput, color: T.grey300, userSelect: "none" }}>
                  {cc(custLang, "searchPlaceholder")}
                </span>
              )}
              <span style={S.micIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.grey500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              </span>
            </div>
          </div>

          {/* Category pills — home tab only */}
          {activeTab === "home" && (
            <div style={S.pillBar} ref={pillBarRef}>
              {categories.map((cat) => (
                <button
                  key={cat}
                  data-cat={cat}
                  style={S.pill(activeCategory === cat)}
                  type="button"
                  onClick={() => scrollToCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Tab content ───────────────────────────────────────────────────── */}
        <div style={{ flex: 1 }}>
          {activeTab === "home"       && !showOrdersPage && renderHomeTab()}
          {activeTab === "search"     && !showOrdersPage && renderSearchTab()}
          {activeTab === "cart"       && !showOrdersPage && renderCartTab()}
          {activeTab === "categories" && !showOrdersPage && renderCategoriesTab()}
          {activeTab === "profile"    && !showOrdersPage && renderProfileTab()}
          {activeTab === "profile"    && showOrdersPage  && renderOrdersPage()}
        </div>

        {/* ── Sticky order bar ──────────────────────────────────────────────── */}
        {totalItems > 0 && (
          <div style={S.orderBarWrap}>
            <button style={S.orderBarBtn} type="button" onClick={() => setShowModal(true)}>
              <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
                <span>{totalItems} item{totalItems !== 1 ? "s" : ""} · ₹{totalPrice.toFixed(2).replace(/\.00$/, "")}</span>
                <span style={{ fontSize: 11, fontWeight: 400, color: deliveryCharge === 0 ? T.green : T.grey300 }}>
                  {deliveryCharge === 0 ? "Free delivery" : `+ ₹${formatMoneyAmount(deliveryCharge)} delivery`}
                </span>
              </span>
              <span>Place Order →</span>
            </button>
          </div>
        )}

        {/* ── Activity bar — live order status overlay ──────────────────────── */}
        {activeTab === "home" && placedOrderId && liveOrderStatus && liveOrderStatus !== "delivered" && totalItems === 0 && (
          <ActivityBar status={liveOrderStatus} lang={custLang} onTap={() => setShowSuccess(true)} />
        )}

        {/* ── Bottom tab bar ────────────────────────────────────────────────── */}
        <div style={S.tabBar}>
          {TABS.map(({ id, Icon, label }) => {
            const active = activeTab === id;
            const color  = active ? T.black : T.grey300;
            return (
              <button
                key={id}
                style={S.tabItem(active)}
                type="button"
                onClick={() => switchTab(id)}
                aria-label={label}
                aria-current={active ? "page" : undefined}
              >
                <div style={{ position: "relative" }}>
                  <Icon color={color} />
                  {id === "cart" && totalItems > 0 && (
                    <span style={{ ...S.cartBadge, position: "absolute", top: -6, right: -8 }}>{totalItems}</span>
                  )}
                </div>
                {/* Always labelled: showing the label only on the active tab left
                    the other three as unlabelled icons, which is both unusual for
                    this class of app and unreadable to a screen reader. */}
                <span style={S.tabLabel(active)}>{label}</span>
              </button>
            );
          })}
        </div>

        {/* ── Order / details modal — bottom sheet ──────────────────────────── */}
        {showModal && (
          <div style={S.overlay} onClick={closeCheckoutSheet}>
            <div style={S.sheet} className="slide-up" onClick={(e) => e.stopPropagation()} {...getSheetDragHandlers(closeCheckoutSheet)}>
              <button style={S.sheetHandleBtn} type="button" aria-label="Close checkout" onClick={closeCheckoutSheet}>
                <span style={S.sheetHandle} />
              </button>
              <>
                  <p style={S.sheetTitle}>Your Order</p>

                  <div style={{ marginBottom: 12 }}>
                    {cartItems.map((i) => <ModalCartItemRow key={i.id} item={i} onUpdate={updateQty} />)}
                  </div>

                  {basketSuggestions.length > 0 && (
                    <div style={{ margin: "4px -20px 14px" }}>
                      <p style={{ ...S.featuredTitle, marginTop: 0 }}>{cc(custLang, "completeBasket")}</p>
                      <div style={S.featuredScroll}>
                        {basketSuggestions.slice(0, 6).map((p) => <FeaturedCard key={p.id} product={p} onTap={() => updateQty(p.id, 1)} />)}
                        <div style={{ minWidth: 16, flexShrink: 0 }} />
                      </div>
                    </div>
                  )}

                  <div style={{ padding: "8px 0 14px", marginBottom: 14 }}>
                    <div style={S.billRow}><span>Items ({totalItems})</span><span>₹{totalPrice.toFixed(2).replace(/\.00$/, "")}</span></div>
                    {deliveryCharge === 0
                      ? <div style={{ ...S.billRow }}><span>Delivery</span><span style={{ color: T.green, fontWeight: 700 }}>FREE</span></div>
                      : <div style={S.billRow}><span>Delivery</span><span>₹{formatMoneyAmount(deliveryCharge)}</span></div>
                    }
                    <div style={S.billDivider} />
                    <div style={S.billTotal}><span>Total</span><span>₹{grandTotal.toFixed(2).replace(/\.00$/, "")}</span></div>
                    {totalSavings > 0 && (
                      <div style={S.savingsPill}>
                        You saved ₹{formatMoneyAmount(totalSavings)} on this order
                      </div>
                    )}
                  </div>

                  <div style={S.formGroup}>
                    <label style={S.formLabel}>Your Name *</label>
                    <input style={S.formInput} type="text" placeholder="Enter your name" value={name} onChange={(e) => setName(e.target.value)} />
                  </div>

                  <div style={S.formGroup}>
                    <label style={S.formLabel}>Delivery Address *</label>
                    <input
                      ref={addressInputRef}
                      style={S.formInput}
                      type="text"
                      autoComplete="off"
                      placeholder="Flat no., building, street, landmark"
                      value={address}
                      onChange={(e) => {
                        setAddress(e.target.value);
                        setLocationError("");
                      }}
                    />
                    {mapsEnabled && (
                      <>
                        <div style={{ position: "relative" }}>
                          {/* When Maps fails, Google renders its own error UI
                              (a wall of broken-image icons) inside this node.
                              Collapse it rather than dimming it, or that markup
                              shows through and lands on the address field. */}
                          <div
                            ref={mapCanvasRef}
                            data-no-sheet-drag="true"
                            style={
                              mapsError
                                ? { height: 0, overflow: "hidden", visibility: "hidden" }
                                : { ...S.mapPreview, opacity: mapsReady ? 1 : 0.35 }
                            }
                            aria-hidden={mapsError ? "true" : undefined}
                            aria-label={mapsError ? undefined : "Delivery location map"}
                          />
                          {!mapsReady && !mapsError && (
                            <div style={{ ...S.mapLoading, position: "absolute", inset: 0, marginTop: 10 }}>
                              Loading map
                            </div>
                          )}
                        </div>
                        {mapsError && (
                          <div style={S.mapFallback}>
                            <p style={S.mapFallbackTitle}>Map unavailable</p>
                            <p style={S.mapFallbackText}>
                              No problem, we'll deliver to the address you typed above.
                            </p>
                          </div>
                        )}
                        {/* Pin controls are meaningless once the map failed:
                            there is nothing to drop a pin on. */}
                        <div style={{ ...S.pinStatus, display: mapsError ? "none" : undefined }}>
                          <p style={{ ...S.locationHint, color: deliveryLocation ? T.green : T.grey500 }}>
                            {deliveryLocation ? "Delivery pin set. Drag the pin to adjust." : "Choose an address suggestion or set your pin."}
                          </p>
                          <button
                            type="button"
                            data-no-sheet-drag="true"
                            onClick={handleUseCurrentLocation}
                            disabled={locationLoading || !mapsReady}
                            style={{
                              ...S.locationBtn,
                              opacity: (locationLoading || !mapsReady) ? 0.6 : 1,
                              color: deliveryLocation?.source === "browser_geolocation" ? T.green : T.black,
                            }}
                          >
                            {locationLoading ? "Locating..." : "Use current location"}
                          </button>
                        </div>
                        {/* mapsError is already explained by the fallback panel
                            above; repeating it in red just alarms the customer
                            about something that no longer blocks their order. */}
                        {locationError && (
                          <p style={{ ...S.locationHint, color: T.red, marginTop: 6 }}>
                            {locationError}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  <button style={S.placeOrderBtn} type="button" onClick={handleFormConfirm}>
                    Confirm Order
                  </button>
                </>
            </div>
          </div>
        )}

        {/* ── Summary / confirm modal ───────────────────────────────────────── */}
        {showSummaryModal && (
          <div style={S.overlay} onClick={closeSummarySheet}>
            <div style={S.sheet} className="slide-up" onClick={(e) => e.stopPropagation()} {...getSheetDragHandlers(closeSummarySheet)}>
              <button style={S.sheetHandleBtn} type="button" aria-label="Close order confirmation" onClick={closeSummarySheet}>
                <span style={S.sheetHandle} />
              </button>
              <div style={S.sheetHeader}>
                <button type="button" style={S.sheetTextBtn} onClick={goBackToCheckout}>Back</button>
                <p style={{ ...S.sheetTitle, margin: 0 }}>Confirm Order</p>
                <button type="button" style={S.sheetTextBtn} onClick={closeSummarySheet}>Close</button>
              </div>

              <div style={{ marginBottom: 10 }}>
                {cartItems.map((i) => (
                  <div key={i.id} style={{ ...S.billRow, padding: "5px 0" }}>
                    <span>{i.name} × {i.quantity}</span>
                    <span style={{ color: T.black, fontWeight: 600 }}>₹{getLineTotal(i).toFixed(2).replace(/\.00$/, "")}</span>
                  </div>
                ))}
              </div>

              <div style={S.billDivider} />
              <div style={S.billRow}><span>Items</span><span>₹{totalPrice.toFixed(2).replace(/\.00$/, "")}</span></div>
              <div style={S.billRow}>
                <span>Delivery</span>
                {deliveryCharge === 0
                  ? <span style={{ color: T.green, fontWeight: 700 }}>FREE</span>
                  : <span>₹{formatMoneyAmount(deliveryCharge)}</span>
                }
              </div>
              <div style={S.billDivider} />
              <div style={S.billTotal}><span>Total</span><span>₹{grandTotal.toFixed(2).replace(/\.00$/, "")}</span></div>

              <div style={S.deliveryConfirmCard}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={S.deliveryConfirmTitle}>Delivering to {name}</p>
                  <p style={S.deliveryConfirmText}>
                    {address || "No delivery address entered"}
                  </p>
                  {mapsEnabled && deliveryLocation && (
                    <p style={{ ...S.deliveryConfirmText, color: T.green, marginTop: 4 }}>
                      Delivery pin added
                    </p>
                  )}
                </div>
                <button type="button" style={S.deliveryEditBtn} onClick={goBackToCheckout}>Edit</button>
              </div>
              <label style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                background: T.offWhite, borderRadius: T.radiusMd,
                padding: "10px 12px", margin: "12px 0 4px",
                cursor: "pointer"
              }}>
                <input
                  type="checkbox"
                  checked={reminderOptIn}
                  onChange={(e) => setReminderOptIn(e.target.checked)}
                  style={{ marginTop: 2, width: 16, height: 16, accentColor: T.orange }}
                />
                <span style={{ fontSize: 12, color: T.grey500, lineHeight: 1.45, fontFamily: T.fontBody }}>
                  Send helpful WhatsApp reminders for repeat grocery items. You can turn this off from Profile.
                </span>
              </label>
              {orderError && <p style={{ color: T.red, fontSize: 13, margin: "8px 0", fontFamily: T.fontBody }}>{orderError}</p>}

              <button
                style={{ ...S.placeOrderBtn, opacity: orderLoading ? 0.6 : 1 }}
                type="button"
                disabled={orderLoading}
                onClick={handleSendOrder}
              >
                {orderLoading ? "Placing Order…" : "Place Order"}
              </button>
            </div>
          </div>
        )}

        {/* ── Success screen ────────────────────────────────────────────────── */}
        {showSuccess && (() => {
          const STATUS_STEPS = ["pending", "accepted", "preparing", "out_for_delivery", "delivered"];
          const STATUS_STEP_LABEL = { pending: "Placed", accepted: "Accepted", preparing: "Preparing", out_for_delivery: "On the Way", delivered: "Delivered" };
          const currentIdx = STATUS_STEPS.indexOf(liveOrderStatus ?? "pending");
          return (
            <div style={{ ...S.successOverlay, overflow: "hidden", overflowY: "auto", justifyContent: "flex-start", paddingTop: 60 }}>
              {/* Confetti */}
              {[
                { color: "#E8470A", left: "20%", delay: "0s"    },
                { color: "#1A6B3C", left: "40%", delay: "0.15s" },
                { color: "#D4A017", left: "60%", delay: "0.05s" },
                { color: "#C23608", left: "75%", delay: "0.2s"  },
                { color: "#9B1C1C", left: "30%", delay: "0.1s"  },
              ].map((c, i) => (
                <div key={i} className="confetti-dot" style={{ background: c.color, left: c.left, top: "10%", animationDelay: c.delay }} />
              ))}

              <div style={S.successCheckCircle} className="check-pop">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <p style={S.successTitle}>Order Placed! 🎉</p>
              <p style={{ ...S.successSubtitle, marginBottom: 8 }}>Your order has been received.</p>

              {/* Warm messages */}
              <div style={{ background: T.offWhite, borderRadius: T.radiusMd, padding: "12px 16px", marginBottom: 24, width: "100%", maxWidth: 340, boxSizing: "border-box" }}>
                <p style={{ fontSize: 13, color: T.grey500, fontFamily: T.fontBody, margin: "0 0 6px", lineHeight: 1.5 }}>💳 Please pay the delivery person on arrival</p>
                <p style={{ fontSize: 13, color: T.grey500, fontFamily: T.fontBody, margin: 0, lineHeight: 1.5 }}>🙏 Offer the delivery person a glass of water</p>
              </div>

              {/* Live status tracker */}
              <div style={{ width: "100%", maxWidth: 340, marginBottom: 28, boxSizing: "border-box" }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: T.grey500, textTransform: "uppercase", letterSpacing: "0.7px", fontFamily: T.fontBody, margin: "0 0 14px" }}>Order Status</p>

                {liveOrderStatus === "accepted" && (
                  <p style={{ fontSize: 13, color: T.green, fontWeight: 600, fontFamily: T.fontBody, margin: "0 0 12px" }}>✅ Your order has been accepted!</p>
                )}

                {/* Progress steps */}
                <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                  {STATUS_STEPS.map((step, i) => {
                    const done    = i <= currentIdx;
                    const active  = i === currentIdx;
                    const isLast  = i === STATUS_STEPS.length - 1;
                    return (
                      <React.Fragment key={step}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: isLast ? "none" : "none" }}>
                          <div style={{
                            width: 28, height: 28, borderRadius: "50%",
                            background: done ? (active ? T.orange : T.green) : T.grey100,
                            border: `2px solid ${done ? (active ? T.orange : T.green) : T.grey300}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            transition: "all 0.4s ease",
                            flexShrink: 0,
                          }}>
                            {done && !active && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            )}
                            {active && <div style={{ width: 8, height: 8, borderRadius: "50%", background: T.white }} />}
                          </div>
                          <span style={{ fontSize: 9, color: done ? (active ? T.orange : T.green) : T.grey300, fontFamily: T.fontBody, fontWeight: done ? 600 : 400, textAlign: "center", whiteSpace: "nowrap" }}>
                            {STATUS_STEP_LABEL[step]}
                          </span>
                        </div>
                        {!isLast && (
                          <div style={{ flex: 1, height: 2, background: i < currentIdx ? T.green : T.grey100, margin: "0 4px", marginBottom: 20, transition: "background 0.4s ease" }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              <button style={{ ...S.successBtn, maxWidth: 340 }} type="button" onClick={() => { setShowSuccess(false); setActiveTab("home"); }}>
                Back to Home
              </button>
            </div>
          );
        })()}

      </div>

      {/* ── Product detail sheet ────────────────────────────────────────── */}
      {detailProduct && (
        <ProductDetailSheet
          product={detailProduct}
          quantity={cart[detailProduct.id] || 0}
          onUpdate={updateQty}
          onClose={() => setDetailProduct(null)}
          related={detailRelated}
          onOpenRelated={(p) => setDetailProduct(p)}
          wishlisted={wishlist.has(detailProduct.id)}
          onWishlist={toggleWishlist}
        />
      )}

      {/* ── Added-to-cart confirmation ──────────────────────────────────── */}
      {addedToast && (
        <div style={S.addedToast} role="status" aria-live="polite" className="fade-up">
          <span style={S.addedToastCheck} aria-hidden="true">✓</span>
          <span style={S.addedToastText}>Added · {addedToast.name}</span>
        </div>
      )}

      {/* ── Bear on bicycle animation ───────────────────────────────────── */}
      <BearRideOverlay show={showBearAnimation} />

      {/* ── Star rating modal ──────────────────────────────────────────── */}
      <RatingModal
        show={showRatingModal}
        orderId={ratedOrderId || placedOrderId}
        stars={ratingStars}
        comment={ratingComment}
        onStars={setRatingStars}
        onComment={setRatingComment}
        onSubmit={handleRatingSubmit}
        onSkip={() => setShowRatingModal(false)}
      />

      {showInstallPrompt && (
        <div style={{
          position: "fixed", bottom: "calc(80px + env(safe-area-inset-bottom))", left: "50%",
          transform: "translateX(-50%)",
          width: "calc(100% - 32px)", maxWidth: 448,
          background: T.white, borderRadius: T.radiusMd,
          boxShadow: T.shadowLg, padding: "16px",
          zIndex: 900, boxSizing: "border-box",
          border: `1px solid ${T.grey100}`,
          display: "flex", flexDirection: "column", gap: 12
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <p style={{ fontFamily: T.fontDisplay, fontSize: 16, fontWeight: 700,
                color: T.black, margin: "0 0 4px" }}>Add to Home Screen</p>
              <p style={{ fontFamily: T.fontBody, fontSize: 13, color: T.grey500, margin: 0 }}>
                {isIOSInstall
                  ? 'Tap the Share button (□↑) in Safari, then "Add to Home Screen"'
                  : "Order faster - add KiranaOS to your home screen"}
              </p>
            </div>
            <button type="button" onClick={() => setShowInstallPrompt(false)}
              style={{ background: "transparent", border: "none", cursor: "pointer",
                fontSize: 18, color: T.grey500, padding: 0, lineHeight: 1 }}>
              ×
            </button>
          </div>
          {!isIOSInstall && (
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button"
                onClick={async () => {
                  const promptEvent = installPromptRef.current;
                  if (!promptEvent) {
                    setShowInstallPrompt(false);
                    return;
                  }
                  await promptEvent.prompt();
                  await promptEvent.userChoice;
                  installPromptRef.current = null;
                  setShowInstallPrompt(false);
                }}
                style={{ ...S.placeOrderBtn, flex: 1, height: 44, fontSize: 14 }}>
                Add to Home Screen
              </button>
              <button type="button" onClick={() => setShowInstallPrompt(false)}
                style={{ flex: 1, height: 44, background: T.grey100, border: "none",
                  borderRadius: T.radiusPill, fontSize: 14, fontWeight: 600,
                  color: T.grey500, cursor: "pointer", fontFamily: T.fontBody }}>
                Not now
              </button>
            </div>
          )}
          {isIOSInstall && (
            <div style={{ display: "flex", alignItems: "center", gap: 12,
              background: T.offWhite, borderRadius: T.radiusMd, padding: "10px 14px" }}>
              <span style={{ fontSize: 24 }}>□↑</span>
              <div>
                <p style={{ fontFamily: T.fontBody, fontSize: 12, fontWeight: 700,
                  color: T.black, margin: "0 0 2px" }}>1. Tap Share</p>
                <p style={{ fontFamily: T.fontBody, fontSize: 12,
                  color: T.black, margin: "0 0 2px" }}>2. Scroll down</p>
                <p style={{ fontFamily: T.fontBody, fontSize: 12,
                  color: T.black, margin: 0 }}>3. Tap "Add to Home Screen"</p>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
